/**
 * Orders Management Component
 * Main orders table with filtering, sorting, bulk operations, and CRUD functionality.
 * Used by Admin, Team Leaders, and Order Desk roles.
 * 
 * REFACTORED: Filter and assignment components extracted to /components/orders/management/
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { formatDateOnlyET, formatDateOnly } from '../utils/formatters';
// Property-field placeholder filter — Feb 25, 2026 (P1 dirty-data fix).
// Centralised in `/app/frontend/src/utils/textFormat.js` so EVERY view
// (Orders dashboard, Order Details, Reports, …) renders the same row
// identically regardless of source (manual entry vs. bulk upload).
import {
  isMeaningfulValue,
  formatStreetAddress,
  formatCity,
  formatCounty,
  formatState,
  formatFullAddress,
  EMPTY_PLACEHOLDER,
} from '../utils/textFormat';
import { 
  Search, Plus, Download, Filter, X, ChevronDown, ChevronUp, FilePlus2,
  MoreHorizontal, RefreshCw, Check, Clock, AlertCircle,
  FileText, Users, Building2, MapPin, Calendar, User,
  ArrowUpDown, Settings, Eye, Edit, Trash2, Copy,
  CheckCircle, Circle, Loader2, TrendingUp, Upload, Flag
} from 'lucide-react';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';

// Custom Components
import { useAuth, OrderDeskEditModal } from '../App';
import { MLS_BUCKET_TABS } from '../components/mls/rollupConfig';
import { StatusBadge, normalizeStatusForOrderDesk } from '../components/StatusBadge';
import OrderSummaryPopup from '../components/OrderSummaryPopup';
import AssignOrdersModal from '../components/AssignOrdersModal';
import EditRequestModal from '../components/EditRequestModal';
import { CreateOrderForm } from '../components/forms';
import { uploadPriorSearchPackage } from '../components/forms/PriorSearchPackagePicker';
import { CreateCustomerForm } from '../components/forms';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { usePermissions } from '../hooks/usePermissions';
import { useWorkspacePresentation } from '../hooks/useWorkspacePresentation';
import { useTableSort } from '../hooks/useTableSort';
import { useAssigneeList, ASSIGNEE_LIST_STALE_MS } from '../hooks/useAssigneeList';
import BulkUploadModal from '../components/orders/BulkUploadModal';
import DuplicateOrderModal from '../components/orders/DuplicateOrderModal';

// Extracted Order Management Components
import { OrderFiltersBar } from '../components/orders/management/OrderFilters';
import { OrderScopeFilters, OrderScopeChips } from '../components/orders/management/OrderScopeFilters';
import { BulkAssignmentControls } from '../components/orders/management/BulkAssignment';
import { BulkPriorityControls } from '../components/orders/management/BulkPriority';
import { OrdersTable, PaginationControls } from '../components/orders/management/OrdersTable';
import { SearchLatencyBadge } from '../components/orders/management/SearchLatencyBadge';

// Utils
import { isTaxProduct } from '../utils/orderHelpers';
import { getBusinessYear, formatBusinessDateTime, formatBusinessDateShort } from '../utils/timezone';

// Performance Strip Component (Inline Performance Awareness)
import OperationalDashboard from '../components/OperationalDashboard';

// Scroll to Top Button
import ScrollToTopButton from '../components/ui/ScrollToTopButton';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Normalize role display names to canonical keys (defense-in-depth for cached sessions)
const DISPLAY_TO_KEY = {
  'Admin': 'admin', 'Super Admin': 'admin', 'Order Desk': 'order_desk',
  'Tax Processor': 'processor', 'Search Processor': 'search_processor',
  'Tax Search Team Leader': 'tax_search_team_leader',
  'Title Search Team Leader': 'title_search_team_leader',
  'Jurisdiction Manager': 'jurisdiction_manager', 'QC Specialist': 'qc_specialist',
  'IT Support': 'it_support', 'IT Manager': 'it_manager',
  'HR': 'hr', 'HR Manager': 'hr_manager', 'Accounting': 'accounting',
};
const nRole = (r) => DISPLAY_TO_KEY[r] || r || '';

// MLS-ASSIGN-3B: split a selection into dual MLS orders (component-assigned
// only) vs normally assignable orders. Orders not present in the current
// page's `orders` state fall into `rest` — the backend guard rejects those
// with an explicit per-order failure, so nothing is silently skipped.
const splitDualMlsSelection = (ids, orders) => {
  const dual = [];
  const rest = [];
  ids.forEach((id) => {
    const o = orders.find((x) => x.id === id);
    if (o?.components?.tax && o?.components?.mls) dual.push(id);
    else rest.push(id);
  });
  return { dual, rest };
};

// Helper function to format property address — uses the shared title-case
// pipeline so `MIAMI, FL 33101` and `Miami, FL 33101` render identically,
// and so legacy uppercase rows in MongoDB display in the same Title Case
// as manual entries without any backfill.
const formatPropertyAddress = (order) => {
  if (!order) return EMPTY_PLACEHOLDER;

  try {
    // Priority 1: backend-composed display string (already cleaned up
    // server-side). Pass it through formatFullAddress's Title-Case stage
    // by splitting on commas — but if it already contains lowercase chars
    // we trust it as-is.
    const display = order.property_address_display;
    if (isMeaningfulValue(display)) {
      // Quick "is it ALL CAPS?" check — if so, run it through formatter
      // by re-splitting. Otherwise return verbatim.
      const isAllCaps = display === display.toUpperCase() && /[A-Z]/.test(display);
      return isAllCaps ? toTitleCaseAddressLine(display) : display;
    }

    // Priority 2: compose from structured parts using the shared helper.
    return formatFullAddress({
      street: order.property_address || order.property_address_full || order.property_address_line1,
      city:   order.property_city,
      state:  order.property_state || order.state,
      zip:    order.property_zip || order.property_zip_code,
    });
  } catch (error) {
    console.error('Error formatting property address:', error);
    return formatStreetAddress(order?.property_address);
  }
};

// Helper used only when the backend sends a pre-composed display string
// in ALL CAPS (legacy rows). Walks comma-separated parts; the part that
// matches "STATE [ZIP]" gets state-uppercased + zip preserved.
const _STATE_ZIP_RE = /^([A-Z]{2})(?:\s+(\S+))?$/i;
const toTitleCaseAddressLine = (line) => {
  const parts = String(line).split(',').map((p) => p.trim()).filter(Boolean);
  return parts.map((part, idx) => {
    // Last part might be "STATE ZIP"
    if (idx === parts.length - 1) {
      const m = _STATE_ZIP_RE.exec(part);
      if (m) {
        return m[2] ? `${m[1].toUpperCase()} ${m[2]}` : m[1].toUpperCase();
      }
    }
    return formatStreetAddress(part);
  }).join(', ');
};

const OrdersManagement = ({ activeView, setActiveView, refreshStats, onOrderTypeFilterChange, onUnassignedOnlyChange, onYearChange }) => {
  // VERSION: 2025-10-19-v2 - Admin UI cleanup applied
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ============================================================================
  // URL STATE PERSISTENCE - Read initial values from URL params
  // ============================================================================
  const validStatuses = ['all', 'open', 'in_progress', 'pending_correction', 'mail_away', 'mail_away_received', 'qc_review', 'typing', 'clarification', 'clarification_received',
    ...MLS_BUCKET_TABS.map((t) => t.value)];
  
  const getInitialFromURL = (param, defaultValue, validValues = null) => {
    const value = searchParams.get(param);
    if (value === null) return defaultValue;
    if (validValues && !validValues.includes(value)) return defaultValue;
    return value;
  };
  
  // Initialize all filter states from URL params
  const getInitialStatusFilter = () => getInitialFromURL('status', 'all', validStatuses);
  const getInitialSearchQuery = () => getInitialFromURL('search', '');
  const getInitialPriorityFilter = () => getInitialFromURL('priority', 'all', ['all', 'normal', 'rush', 'urgent']);
  const getInitialOrderTypeFilter = () => getInitialFromURL('type', 'all', ['all', 'tax', 'search']);
  const getInitialUnassignedOnly = () => getInitialFromURL('unassigned', 'false') === 'true';
  const getInitialYearFilter = () => {
    const urlYear = searchParams.get('year');
    if (urlYear === 'all') return 'all';
    // Validate year format (4 digits)
    if (urlYear && /^\d{4}$/.test(urlYear)) return urlYear;
    return getBusinessYear().toString();
  };
  
  // Function to update URL params without navigation
  const updateURLParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      // Remove param if it's the default value
      const defaults = {
        status: 'all',
        search: '',
        priority: 'all',
        type: 'all',
        unassigned: 'false',
        year: getBusinessYear().toString()
      };
      
      if (value === defaults[key] || value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    // Update URL without triggering navigation/reload
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [processors, setProcessors] = useState([]);
  
  // State for Order Summary Popup (Tax Search/Update Tax Search only)
  const [orderSummaryPopup, setOrderSummaryPopup] = useState({ isOpen: false, orderData: null });
  
  // State for Edit Request Modal (Order Desk workflow for assigned orders)
  const [editRequestModal, setEditRequestModal] = useState({ isOpen: false, orderData: null });
  
  // Standardized status enum - matches backend
  const ORDER_STATUS = {
    CREATED: 'created',
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    MAIL_AWAY: 'mail_away',
    MAIL_AWAY_RECEIVED: 'mail_away_received',
    QC_REVIEW: 'qc_review',
    COMPLETE: 'complete',
    CLARIFICATION: 'clarification',
    CLARIFICATION_RECEIVED: 'clarification_received',
    CANCELLED: 'cancelled'
  };
  
  // Status tabs configuration - MOVED below useAuth() hook to avoid ReferenceError
  // (STATUS_TABS defined after useAuth() call below)
  
  // Normalize assignee helper - handles null/undefined and different field names
  const normalizeAssignee = (assignee) => {
    if (!assignee || !assignee.id) {
      return { id: null, label: 'Unassigned' };
    }
    
    const label = 
      assignee.full_name?.trim() ||
      [assignee.first_name, assignee.last_name].filter(Boolean).join(' ').trim() ||
      'Unassigned';
    
    return { id: assignee.id, label };
  };
  
  // Normalize processor for dropdown options
  const normalizeProcessor = (proc) => ({
    value: proc.id,
    label: proc.full_name?.trim() || 
           [proc.first_name, proc.last_name].filter(Boolean).join(' ').trim() || 
           proc.email ||
           'Unnamed'
  });
  
  // Shared cell rendering logic for consistent display across personas
  const getCellContent = (order, columnId) => {
    switch (columnId) {
      case 'order_number':
        return order.order_number || '-';
      
      case 'client_code':
        // Show 4-char badge with tooltip
        const shortCode = order.client_short_code || order.client_code || '-';
        return (
          <Badge 
            variant="outline" 
            className="font-mono text-xs cursor-help"
            title={`Full code: ${order.client_code || 'Unknown'}`}
          >
            {shortCode}
          </Badge>
        );
      
      case 'client_product':
      case 'product':
        return order.client_product_name_snapshot || order.product_name || order.product_id || '-';
      
      case 'customer':
        return order.borrower_name || order.customer_name || '-';
      
      case 'property':
        // Use full formatted address from backend (Street, City, State ZIP)
        const address = order.property_address_display || order.property_address || order.property_address_full || order.address || '-';
        return <span className="truncate block" title={address}>{address}</span>;
      
      case 'county':
        return order.county || '-';
      
      case 'state':
        return order.state || '-';
      
      case 'status':
        return (
          <StatusBadge status={order.status} userRole={user?.role} orderData={order} />
        );
      
      case 'assignee':
      case 'actions':
        // TYPING-ASSIGNEE-001: same stage rule as OrdersTable — Typing shows the Typing Processor.
        if (order.status === 'typing') {
          const typist = order.typing_assigned_to ? (order.typing_assigned_to_name || '').trim() : '';
          return typist || <span className="text-muted-foreground italic">Unassigned</span>;
        }
        // QC-ASSIGNEE-001: QC Review shows the QC claimant once claimed (Search Processor untouched).
        if (order.status === 'qc_review' && order.qc_assigned_to) {
          return (
            <span title={`Search Processor: ${order.assignee_name || (order.assignee_id ? 'Assigned' : 'Unassigned')}`}>
              {(order.qc_assigned_to_name || '').trim() || 'QC Specialist'}
              <span className="ml-1.5 rounded bg-fuchsia-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700">QC</span>
            </span>
          );
        }
        // Get the assignee name first
        const rawAssigneeName = order.assignee_name || 
          normalizeAssignee(order.assignee || { id: order.assignee_id }).label;
        
        // Check if effectively unassigned:
        // 1. No assignee_id, OR
        // 2. Has ID but name resolves to "Unassigned" (orphaned reference)
        const hasNoAssignee = !order.assignee_id;
        const hasOrphanedAssignee = !rawAssigneeName || 
          rawAssigneeName === 'Unassigned' || 
          rawAssigneeName === 'unassigned';
        
        if (hasNoAssignee || hasOrphanedAssignee) {
          // Always render unassigned with muted/secondary style
          return <span className="text-muted-foreground italic">Unassigned</span>;
        }
        
        return rawAssigneeName;

      case 'order_date': {
        // Order Date column — Feb 25, 2026. Sources from order.created_at,
        // formatted as "Apr 12, 2026" in the account timezone (ET) so the
        // value matches the rest of the lifecycle timestamps in the app.
        const raw = order.created_at || order.received_date;
        if (!raw) return <span className="text-slate-400">—</span>;
        return (
          <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">
            {formatDateOnlyET(raw)}
          </span>
        );
      }

      case 'created_date': {
        const raw = order.created_at || order.received_date;
        if (!raw) return '-';
        try {
          return formatDateOnly(raw);
        } catch { return '-'; }
      }

      case 'priority': {
        const isRush = order.priority === 'rush' || order.is_rush;
        return isRush
          ? <Badge className="bg-red-100 text-red-700 text-[10px]">RUSH</Badge>
          : <span className="text-muted-foreground text-xs">Normal</span>;
      }
      
      default:
        return '-';
    }
  };
  
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const createOrderDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Custom close handler for Create Order modal — prevents accidental data loss
  const handleCreateOrderOpenChange = useCallback((open) => {
    if (open) {
      createOrderDirtyRef.current = false;
      setShowCreateOrder(true);
      return;
    }
    // Closing — check for unsaved data
    if (createOrderDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      setShowCreateOrder(false);
    }
  }, []);

  // Confirm discard — close both confirmation and form
  const handleDiscardConfirm = useCallback(() => {
    createOrderDirtyRef.current = false;
    setShowDiscardConfirm(false);
    setShowCreateOrder(false);
  }, []);

  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [tableDensity, setTableDensity] = useState('normal'); // 'compact', 'normal', 'comfortable'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  // PROD-RESIL-001 (Jul 2026): a failed initial fetch used to be
  // indistinguishable from a legit empty result ("no records to display").
  const [fetchError, setFetchError] = useState(false);
  const { searchInput: searchInputDisplay, setSearchInput: setSearchInputDisplay, debouncedValue: searchQuery, setDebouncedValue: setSearchQueryDirect, clearSearch: clearSearchState } = useDebouncedSearch(getInitialSearchQuery(), 600);
  const [isSearching, setIsSearching] = useState(false);
  // Admin-only diagnostic telemetry from the `/orders-v2` response.
  // Surfaces a `Search: {ms}ms • {path}` pill in the dashboard header
  // so the Ops team can validate the search-index rollout in real time.
  const [searchMeta, setSearchMeta] = useState(null);
  const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter); // Status filter from URL or 'all'
  const [priorityFilter, setPriorityFilter] = useState(getInitialPriorityFilter); // Priority from URL
  // MLS-VIEW-SPLIT: Tax TL segmented working view — 'tax' (default) | 'mls'
  const [mlsView, setMlsView] = useState(() => getInitialFromURL('view', 'tax', ['tax', 'mls']));
  const mlsViewRef = useRef(null);
  if (mlsViewRef.current === null) mlsViewRef.current = getInitialFromURL('view', 'tax', ['tax', 'mls']);
  // ORDER-FILTERS-001: TL Client Code / Product scope filters (URL-persisted, ref for fresh reads in fetchOrders)
  const [scopeFilters, setScopeFilters] = useState(() => ({
    client_code: searchParams.get('client_code') || '',
    client_product: searchParams.get('product') || '',
  }));
  const scopeFiltersRef = useRef(null);
  if (scopeFiltersRef.current === null) scopeFiltersRef.current = { ...scopeFilters };
  const [orderTypeFilter, setOrderTypeFilter] = useState(getInitialOrderTypeFilter); // Order type from URL
  const [unassignedOnly, setUnassignedOnly] = useState(getInitialUnassignedOnly); // Unassigned from URL
  const [selectedOrders, setSelectedOrders] = useState(new Set()); // Bulk selection for Admin - using Set for persistence
  const [bulkAssignee, setBulkAssignee] = useState(''); // Bulk assignment target for Admin
  
  // ──────────────────────────────────────────────────────────────────────
  // Feb 27, 2026 — P0 Search synchronization (AVR bug RCA fixes #2/#3/#4):
  // Track every in-flight `fetchOrders` request so we can:
  //   • Cancel the previous request when a new one starts (AbortController)
  //   • Ignore stale resolve handlers (request-ID guard before any setState)
  //   • Skip the success toast when the user has typed further since submit
  // BOTH refs MUST be checked BEFORE any setState in fetchOrders so a slow
  // 30s response can never overwrite a faster newer one. The pattern:
  //   const myId = ++fetchRequestIdRef.current;
  //   const myCtrl = new AbortController();
  //   fetchControllerRef.current?.abort();
  //   fetchControllerRef.current = myCtrl;
  //   ...await axios.get(url, { signal: myCtrl.signal })...
  //   if (fetchRequestIdRef.current !== myId) return;   // stale → bail
  // ──────────────────────────────────────────────────────────────────────
  const fetchControllerRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  // SEARCH-PERF-001 (Jun 2026) — background-refresh & duplicate-fetch guards:
  //   • userFetchInFlightRef — true while a user-triggered fetch/search is
  //     in flight; the 30s background tick skips instead of aborting it.
  //   • lastFetchSignatureRef — signature of the last user-triggered fetch;
  //     lets the searchParams-driven effect skip the duplicate refetch that
  //     the post-search URL sync used to trigger.
  //   • liveFiltersRef — live snapshot of current filters so the interval
  //     never fetches with stale closure values (e.g. an empty search).
  const userFetchInFlightRef = useRef(false);
  const lastFetchSignatureRef = useRef(null);
  const liveFiltersRef = useRef({});
  
  const { user, logout } = useAuth();
  // RBAC-PILOT-ORDERS-001: additive matrix grants (role capability OR explicit orders:* grant)
  const { hasPermission: hasMatrixPermission } = usePermissions();
  const canCreateOrders = ['order_desk', 'admin'].includes(nRole(user?.role)) || hasMatrixPermission('orders', 'create');
  const canEditOrdersViaMatrix = hasMatrixPermission('orders', 'update');
  const canOpenOrderViaMatrix = hasMatrixPermission('orders', 'view');
  // OD-PARITY-001: presentation flag (Order Desk look) vs capability gates (permissions)
  const { isOrderDeskView } = useWorkspacePresentation(user);
  const canViewReports = ['order_desk', 'admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role)) || hasMatrixPermission('reports', 'view');
  const canBulkUpload = ['admin', 'order_desk'].includes(nRole(user?.role)) || hasMatrixPermission('orders', 'bulk_upload');
  // MLS-VIEW-SPLIT: must be declared AFTER useAuth (TDZ on `user` otherwise)
  const isTaxTL = nRole(user?.role) === 'tax_search_team_leader';
  // ORDER-FILTERS-001: Client Code / Product filters — Team Leader + Admin operations dashboards
  // (Order Desk presentation excluded). Facets come back with every order-list response.
  const hasScopeFilters = ['tax_search_team_leader', 'title_search_team_leader'].includes(nRole(user?.role))
    || (nRole(user?.role) === 'admin' && !isOrderDeskView);
  const [scopeFacets, setScopeFacets] = useState(null);

  const handleScopeFilterChange = (next) => {
    const merged = { ...scopeFiltersRef.current, ...next };
    scopeFiltersRef.current = merged;
    setScopeFilters(merged);
    setCurrentPage(1);
    updateURLParams({ client_code: merged.client_code || '', product: merged.client_product || '' });
    fetchOrders(1, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
  };
  
  // Status tabs configuration - role-aware (must be after useAuth)
  // NOTE: Complete and Cancelled tabs removed from Dashboard (operational work queue)
  // These statuses are only viewable in Reports (historical + audit record)
  // Search roles should not see Mail-Away tabs (Search products don't use Mail-Away workflow)
  const SEARCH_ONLY_ROLES = ['title_search_team_leader'];
  const isSearchOnlyDashboard = SEARCH_ONLY_ROLES.includes(nRole(user?.role)) || orderTypeFilter === 'search';

  // MLS-UNIFIED-STATUS-001: MLS context = Tax TL "MLS ORDERS" view or Admin Order Type = MLS.
  // One unified, exclusive tab row (shared backend predicate) replaces the parent
  // status tabs + the "All MLS States" dropdown.
  const inMlsContext = nRole(user?.role) === 'tax_search_team_leader'
    ? mlsView === 'mls'
    : (nRole(user?.role) === 'admin' && orderTypeFilter === 'mls');

  const STATUS_TABS = isOrderDeskView ? [
    { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
    { value: 'new_today', label: 'New Today', color: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' },
    { value: 'unassigned', label: 'Unassigned', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
    { value: 'assigned_today', label: 'Assigned Today', color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
  ] : inMlsContext ? [
    { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
    ...MLS_BUCKET_TABS,
  ] : (() => {
    const allTabs = [
      { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
      { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
      { value: ORDER_STATUS.IN_PROGRESS, label: 'Pending', color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
      // Jun 2026 — QC Phase 1 (DASH-PARITY-001): dedicated tab so Admin /
      // Team Leaders can drill into soft-returned files from the "Correction
      // Required" KPI tile. DB status remains `pending_correction`; UI label
      // is "Correction Required" for consistency with the badge / KPI.
      { value: 'pending_correction', label: 'Correction Required', color: 'bg-rose-100 text-rose-700 hover:bg-rose-200' },
      { value: ORDER_STATUS.MAIL_AWAY, label: 'Mail-Away', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
      { value: ORDER_STATUS.MAIL_AWAY_RECEIVED, label: 'Mail-Away Received', color: 'bg-pink-100 text-pink-700 hover:bg-pink-200' },
      { value: ORDER_STATUS.QC_REVIEW, label: 'QC Review', color: 'bg-violet-100 text-violet-700 hover:bg-violet-200' },
      // TYPING-001 Phase 2 (Aug 2026) — dedicated tab for files in Typing.
      // TYPING-SCOPE-001: Typing is Title-Search-only — hidden from Tax TL
      // and from Admin's Tax Only / MLS order-type views (visibility only;
      // the Typing workflow/routing itself is unchanged).
      ...(user?.role !== 'tax_search_team_leader'
          && !(nRole(user?.role) === 'admin' && ['tax', 'mls'].includes(orderTypeFilter))
        ? [{ value: 'typing', label: 'Typing', color: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200' }]
        : []),
      { value: ORDER_STATUS.CLARIFICATION, label: 'Clarification', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
      { value: ORDER_STATUS.CLARIFICATION_RECEIVED, label: 'Clarification Received', color: 'bg-amber-100 text-amber-700 hover:bg-amber-200' }
    ];
    if (isSearchOnlyDashboard) {
      return allTabs.filter(t => t.value !== ORDER_STATUS.MAIL_AWAY && t.value !== ORDER_STATUS.MAIL_AWAY_RECEIVED);
    }
    return allTabs;
  })();
  
  // Duplicate order detection state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateOrder, setDuplicateOrder] = useState(null);
  const [pendingOrderData, setPendingOrderData] = useState(null); // Store order data for override
  // DUP-UNIFY-001: terminal match → "Create Anyway" (no reason); pendingEditData
  // → the 409 came from an EDIT (PUT) rather than a create.
  const [duplicateIsTerminal, setDuplicateIsTerminal] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);
  
  // Year-based filtering state - Default to current year for operational efficiency
  // Year filter - use business timezone (America/New_York) for consistent filtering
  const currentYear = getBusinessYear().toString();
  const [yearFilter, setYearFilter] = useState(getInitialYearFilter); // Year from URL or current year
  const [availableYears, setAvailableYears] = useState([currentYear, 'all']); // Available years from API
  const [loadingYears, setLoadingYears] = useState(true);

  // Feb 27, 2026 — REMOVED legacy "clear all selections on filter change"
  // useEffect. It was Option (a) (clear-all) for admin role and conflicted
  // with fix #5 in fetchOrders (Option (c) preserve-and-prune). When admin
  // narrowed a search, BOTH fired: the new prune logic correctly kept the
  // intersection, then the legacy effect immediately wiped them. Result was
  // Assign=(0) every time, plus two competing toasts.
  // fetchOrders L1029-1063 now handles BOTH admin (selectedOrders) and
  // non-admin (selectedOrderIds) paths uniformly via per-Set prune logic.

  // Assignment state for team leaders
  const [availableProcessors, setAvailableProcessors] = useState([]);
  const [processorCacheTimestamp, setProcessorCacheTimestamp] = useState(null);
  
  // Order details modal state for team leaders
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Bulk assignment state for Title Search Team Leader
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignProcessor, setBulkAssignProcessor] = useState('');
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  
  // Custom assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignModalState, setAssignModalState] = useState('confirm'); // 'confirm' | 'progress' | 'success'

  // MLS-ASSIGN-3B: apply component assignments to the selected dual MLS
  // orders. "No change" ('') is a true no-op; '__unassign__' explicitly
  // removes that component's processor only. Never touches order.assignee_id.
  const handleApplyMlsComponents = async ({ dualIds, taxSel, mlsSel }) => {
    const ops = Object.entries({ tax: taxSel, mls: mlsSel }).filter(([, v]) => v !== '');
    if (!dualIds?.length || !ops.length) return;
    // MLS-ASSIGN-CLARITY (UAT): explicit per-component confirmation — the
    // toast names the component and assignee, and fires ONLY after the
    // backend confirms each write.
    const COMP_LABELS = { tax: 'Tax Search', mls: 'MLS Search' };
    const results = { tax: { ok: 0, name: null }, mls: { ok: 0, name: null } };
    let failed = 0;
    for (const orderId of dualIds) {
      for (const [comp, sel] of ops) {
        try {
          if (sel === '__unassign__') {
            await axios.post(`${API}/orders/${orderId}/components/${comp}/unassign`);
            results[comp].ok += 1;
            results[comp].name = 'Unassigned';
          } else {
            const res = await axios.post(`${API}/orders/${orderId}/components/${comp}/assign`, { user_id: sel });
            results[comp].ok += 1;
            results[comp].name = res.data?.assignee_name || 'assigned';
          }
        } catch (e) {
          failed += 1;
          const d = e.response?.data?.detail;
          toast.error(`${COMP_LABELS[comp]} assignment failed`, {
            description: d?.message || (typeof d === 'string' ? d : 'Assignment failed'),
            duration: 6000,
          });
        }
      }
    }
    ['tax', 'mls'].forEach((comp) => {
      const r = results[comp];
      if (r.ok > 0) {
        toast.success(`${COMP_LABELS[comp]} → ${r.name} (${r.ok} order${r.ok !== 1 ? 's' : ''})`);
      }
    });
    fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
    if (refreshStats) refreshStats();
  };
  const [assignModalData, setAssignModalData] = useState({ orderCount: 0, assigneeName: '' });

  // Bulk Upload modal state (Admin and Order Desk only)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isBulkClarificationSending, setIsBulkClarificationSending] = useState(false);
  
  // Handler: Mark/unmark selected orders as Clarification Sent
  const handleBulkClarificationSent = async (mark = true) => {
    const ids = [...selectedOrderIds].length > 0 ? [...selectedOrderIds] : [...selectedOrders];
    if (ids.length === 0) {
      toast.error('No orders selected');
      return;
    }
    setIsBulkClarificationSending(true);
    try {
      const res = await axios.post(`${API}/orders/bulk-clarification-sent`, {
        order_ids: ids,
        mark,
      });
      const { updated_count, skipped_count, skipped } = res.data;
      if (updated_count > 0) {
        toast.success(`${mark ? 'Marked' : 'Unmarked'} ${updated_count} order${updated_count !== 1 ? 's' : ''} as Clarification ${mark ? 'Sent' : 'cleared'}`);
      }
      if (skipped_count > 0) {
        const reasons = skipped.map(s => s.reason).filter((v, i, a) => a.indexOf(v) === i).join(', ');
        toast.info(`${skipped_count} order${skipped_count !== 1 ? 's' : ''} skipped: ${reasons}`);
      }
      setSelectedOrders(new Set());
      setSelectedOrderIds(new Set());
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update clarification sent status');
    } finally {
      setIsBulkClarificationSending(false);
    }
  };
  
  // ========================================
  // SORTING STATE - Global Column Sorting
  // ========================================
  const {
    sortConfig,
    handleSort,
    getSortParams,
    resetSort
  } = useTableSort({
    defaultSortKey: 'created_at',
    defaultSortOrder: 'desc',
    onSortChange: (newSortConfig) => {
      // Refetch orders when sort changes
      setCurrentPage(1); // Reset to first page on sort change
    }
  });

  // ===============================
  // SINGLE SOURCE OF TRUTH FOR ADMIN ORDERS TABLE COLUMNS
  // ===============================
  // ===============================
  // STANDARDIZED COLUMN CONFIGURATIONS
  // ===============================
  
  // Standard column widths per spec: 40|160|110|150|220|136|64|165|180
  // Feb 25, 2026: ORDER_DATE inserted after ORDER_NUMBER for all roles.
  const STANDARD_COLUMNS = {
    CHECKBOX: { id: 'checkbox', title: '', width: '40px', minWidth: '40px', maxWidth: '40px', resizable: false, sortable: false },
    ORDER_NUMBER: { id: 'order_number', title: 'Order #', width: '160px', minWidth: '150px', maxWidth: '180px', sortable: true, clickable: true },
    ORDER_DATE: { id: 'order_date', title: 'Order Date', width: '110px', minWidth: '100px', maxWidth: '130px', sortable: true },
    CLIENT_CODE: { id: 'client_code', title: 'Client Code', width: '110px', minWidth: '100px', maxWidth: '130px', sortable: true },
    PRODUCT: { id: 'client_product', title: 'Client Product', width: '150px', minWidth: '140px', maxWidth: '180px', sortable: true },
    BORROWER: { id: 'customer', title: 'Borrower Name', width: '170px', minWidth: '150px', maxWidth: '230px', sortable: true },
    ADDRESS: { id: 'property', title: 'Property Address', width: '220px', minWidth: '180px', maxWidth: '300px', sortable: true },
    COUNTY: { id: 'county', title: 'County', width: '136px', minWidth: '128px', maxWidth: '160px', sortable: true },
    STATE: { id: 'state', title: 'State', width: '64px', minWidth: '60px', maxWidth: '80px', sortable: true },
    STATUS: { id: 'status', title: 'Status', width: '165px', minWidth: '160px', maxWidth: '185px', sortable: true },
    ASSIGNEE: { id: 'assignee', title: 'Assignee', width: '220px', minWidth: '190px', maxWidth: '280px', sortable: true },
    ACTIONS: { id: 'actions', title: 'Actions', width: '96px', minWidth: '96px', maxWidth: '100px', resizable: false }
  };
  
  // Base columns for Admin Orders
  // Order # | Order Date | Client Code | Client Product | Property Address | County | State | Status | Assignee
  // NOTE: Borrower Name removed - available inside order detail view
  // Feb 25, 2026: ORDER_DATE inserted after ORDER_NUMBER (consistent across roles).
  const adminOrderColumns = [
    STANDARD_COLUMNS.CHECKBOX,
    STANDARD_COLUMNS.ORDER_NUMBER,
    STANDARD_COLUMNS.ORDER_DATE,
    STANDARD_COLUMNS.CLIENT_CODE,
    STANDARD_COLUMNS.PRODUCT,
    STANDARD_COLUMNS.ADDRESS,
    STANDARD_COLUMNS.COUNTY,
    STANDARD_COLUMNS.STATE,
    STANDARD_COLUMNS.STATUS,
    STANDARD_COLUMNS.ASSIGNEE
  ];

  // Tax Team Leader columns - Same as Admin WITH Status column, WITH Assignee column
  // Checkbox | Order # | Order Date | Client Code | Client Product | Property Address | County | State | Status | Assignee
  const taxTeamLeaderColumns = [
    STANDARD_COLUMNS.CHECKBOX,
    STANDARD_COLUMNS.ORDER_NUMBER,
    STANDARD_COLUMNS.ORDER_DATE,
    STANDARD_COLUMNS.CLIENT_CODE,
    STANDARD_COLUMNS.PRODUCT,
    STANDARD_COLUMNS.ADDRESS,
    STANDARD_COLUMNS.COUNTY,
    STANDARD_COLUMNS.STATE,
    STANDARD_COLUMNS.STATUS,
    STANDARD_COLUMNS.ASSIGNEE
  ];

  // Define column configuration for Order Desk table (Processor view)
  // Feb 25, 2026: Assignee column REMOVED (not relevant for processors); 
  // ORDER_DATE inserted after ORDER_NUMBER to keep the column position
  // identical to Admin/TL views for visual consistency.
  // Order # | Order Date | Client Code | Client Product | Property Address | County | State | Status
  const initialOrderColumns = [
    { id: 'order_number',   title: 'Order #',          width: '150px', minWidth: '140px', maxWidth: '170px', sortable: true, fixed: true },
    { id: 'order_date',     title: 'Order Date',       width: '110px', minWidth: '100px', maxWidth: '130px', sortable: true, fixed: true },
    { id: 'client_code',    title: 'Client Code',      width: '100px', minWidth: '90px',  maxWidth: '120px', sortable: true, fixed: true },
    { id: 'client_product', title: 'Client Product',   width: '130px', minWidth: '120px', maxWidth: '160px', sortable: true, fixed: true },
    { id: 'property',       title: 'Property Address', width: '200px', minWidth: '170px', maxWidth: '280px', sortable: true, grow: 1 },
    { id: 'county',         title: 'County',           width: '120px', minWidth: '110px', maxWidth: '150px', sortable: true, fixed: true },
    { id: 'state',          title: 'State',            width: '56px',  minWidth: '52px',  maxWidth: '70px',  sortable: true, fixed: true },
    { id: 'status',         title: 'Status',           width: '140px', minWidth: '130px', maxWidth: '160px', sortable: true, fixed: true },
  ];

  // ===============================
  // COLUMN CONFIGURATIONS
  // ===============================

  // Title Search Team Leader columns - Same as Admin WITH Status column, WITH Assignee column
  // NOTE: Borrower Name removed - available inside order detail view
  const titleSearchTeamLeaderColumns = [
    STANDARD_COLUMNS.CHECKBOX,
    STANDARD_COLUMNS.ORDER_NUMBER,
    STANDARD_COLUMNS.ORDER_DATE,
    STANDARD_COLUMNS.CLIENT_CODE,
    STANDARD_COLUMNS.PRODUCT,
    STANDARD_COLUMNS.ADDRESS,
    STANDARD_COLUMNS.COUNTY,
    STANDARD_COLUMNS.STATE,
    STANDARD_COLUMNS.STATUS,
    STANDARD_COLUMNS.ASSIGNEE
  ];

  // Tax Search Team Leader columns - using old variable name for backward compatibility
  // This is now just an alias to taxTeamLeaderColumns
  const taxSearchTeamLeaderColumns = taxTeamLeaderColumns;

  // Define column configuration for Tax Search Team Leader (with bulk assignment)


  // Select appropriate columns based on user role
  const getInitialColumnsForRole = (userRole) => {
    if (userRole === 'admin') {
      // Admin gets all columns including Status
      return adminOrderColumns;
    } else if (userRole === 'tax_search_team_leader' || userRole === 'team_leader') {
      // Tax Team Leader gets all columns including Status
      return taxTeamLeaderColumns;
    } else if (userRole === 'title_search_team_leader') {
      return titleSearchTeamLeaderColumns;
    } else {
      return initialOrderColumns; // Default for Order Desk, etc.
    }
  };

  // Initialize resizable columns hook with role-specific storage key
  const getTableIdForRole = (userRole) => {
    if (userRole === 'admin' || userRole === 'tax_search_team_leader' || userRole === 'team_leader') {
      // Tax Team Leader and generic team_leader share the same table config as Admin
      return 'adminOrders';
    } else if (userRole === 'title_search_team_leader') {
      return 'titleSearchTeamLeader';
    } else {
      return 'orderDesk'; // Default for Order Desk, etc.
    }
  };

  // Memoize initial columns to prevent new array references on each render
  const memoizedInitialColumns = useMemo(() => getInitialColumnsForRole(user?.role), [user?.role]);
  
  // Use resizable columns for Order Desk dashboard with role-based persistence
  const {
    columns: orderColumns,
    visibleColumns: visibleOrderColumns,
    columnVisibility: orderColumnVisibility,
    isResizing: isOrderTableResizing,
    resizingColumn: resizingOrderColumn,
    tableRef: orderTableRef,
    handleResizeStart: handleOrderResizeStart,
    handleAutoFit: handleOrderAutoFit,
    toggleColumnVisibility: toggleOrderColumnVisibility,
    handleKeyboardResize: handleOrderKeyboardResize,
    resetColumns: resetOrderColumns
  } = useResizableColumns(getTableIdForRole(user?.role), memoizedInitialColumns, user?.role || 'default');



  // May 1, 2026 — bumped default page size from 50 → 100 globally across
  // all roles using this dashboard (Admin / Title TL / Tax TL).
  // The backend `/api/orders-v2` endpoint accepts `limit` up to 100
  // (FastAPI Query `le=100`), so this is the maximum allowed per-page.
  const ORDERS_PER_PAGE = 100;

  // Scroll to top on component mount (e.g., after navigation from ProcessorOrders)
  // This ensures users see the top of the dashboard after status updates
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Clear cached columns for admin and team leader roles when user role changes
  useEffect(() => {
    // Clear all cached column configs to ensure consistent column layout after updates
    localStorage.removeItem('adminOrders_columns');
    localStorage.removeItem('adminOrders_visibility');
    localStorage.removeItem('orderDesk_columns');
    localStorage.removeItem('orderDesk_visibility');
    localStorage.removeItem('titleSearchTeamLeader_columns');
    localStorage.removeItem('titleSearchTeamLeader_visibility');
    localStorage.removeItem('taxSearchTeamLeader_columns');
    localStorage.removeItem('taxSearchTeamLeader_visibility');
    localStorage.removeItem('processor_columns');
    localStorage.removeItem('processor_visibility');
    
    // Force reset columns to ensure clean state
    resetOrderColumns();
  }, [user?.role]);

  // Fetch available years for year filter dropdown
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        setLoadingYears(true);
        // Using v2 API endpoint
        const response = await axios.get(`${API}/orders-v2/years`);
        const currentYearStr = getBusinessYear().toString();
        
        if (response.data?.years?.length > 0) {
          // Add "All Years" option at the end.
          // PERF-DUP-001 (Jul 2026): the API returns years as NUMBERS while
          // yearFilter state is a STRING ("2026"). The strict `includes`
          // check below always missed, so setYearFilter(years[0]) stored a
          // numeric year — defeating the JSON fetch-signature dedupe guard
          // and firing a duplicate /orders-v2 request on every page load.
          // Normalize to strings once here.
          const years = [...response.data.years.map(String), 'all'];
          setAvailableYears(years);
          
          // Default to current year for operational efficiency
          // If current year exists in available years, use it; otherwise use first available year
          if (years.includes(currentYearStr)) {
            setYearFilter(currentYearStr);
          } else if (years.length > 1) {
            // If current year not available, use the most recent year (first in list, excluding 'all')
            setYearFilter(years[0]);
          }
        } else {
          // No years from API - use current year
          setAvailableYears([currentYearStr, 'all']);
          setYearFilter(currentYearStr);
        }
      } catch (error) {
        console.error('Error fetching available years:', error);
        // Fallback - use current year
        const currentYearStr = getBusinessYear().toString();
        setAvailableYears([currentYearStr, 'all']);
        setYearFilter(currentYearStr);
      } finally {
        setLoadingYears(false);
      }
    };
    
    fetchAvailableYears();
  }, []);

  // Track whether this is the initial mount (prevents double-fetch on filter changes)
  const isInitialMountRef = useRef(true);

  // SEARCH-PERF-001 — keep the live filter snapshot fresh on every render so
  // the 30s background interval never fetches with stale closure values.
  liveFiltersRef.current = { currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly };
  
  useEffect(() => {
    if (!user) return;
    
    // Only fetch orders on INITIAL MOUNT — filter changes are handled by onFilterChange callback
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      
      // Read URL params directly to ensure we use the persisted values on first render
      const initialSearch = searchParams.get('search') || '';
      const initialStatus = searchParams.get('status') || 'all';
      const initialPriority = searchParams.get('priority') || 'all';
      const initialType = searchParams.get('type') || 'all';
      const initialYear = searchParams.get('year') || getBusinessYear().toString();
      const initialUnassigned = searchParams.get('unassigned') === 'true';
      
      // Sync state with URL params on mount (in case they diverged)
      if (initialSearch !== searchQuery) {
        setSearchInputDisplay(initialSearch);
        setSearchQueryDirect(initialSearch);
      }
      if (initialStatus !== statusFilter && validStatuses.includes(initialStatus)) setStatusFilter(initialStatus);
      if (initialPriority !== priorityFilter) setPriorityFilter(initialPriority);
      if (initialType !== orderTypeFilter) setOrderTypeFilter(initialType);
      if (initialYear !== yearFilter) setYearFilter(initialYear);
      if (initialUnassigned !== unassignedOnly) setUnassignedOnly(initialUnassigned);
      // May 5, 2026 — push initial yearFilter to parent so dashboard
      // stats cards fetch with the same year on first render.
      if (onYearChange) onYearChange(initialYear);
      // MLS-TAX-SPLIT (Aug 23, 2026): push initial order type to parent so
      // KPI cards scope correctly on deep links like /dashboard?type=tax.
      if (onOrderTypeFilterChange) onOrderTypeFilterChange(initialType);
      
      // Fetch with URL params to ensure filtered results on page load
      fetchOrders(currentPage, initialSearch, initialStatus, initialType, initialPriority, initialYear, initialUnassigned);
      fetchCustomers();
      fetchProperties();
      
      // Only fetch processors for roles that can assign orders (Admin, Team Leaders)
      const canAssignOrders = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role));
      if (canAssignOrders) {
        fetchProcessors();
      }
    }

    // Set up polling for real-time updates (Admin/Team Leader dashboard)
    // Polling interval is recreated when filters change to capture latest values
    
    // If sortConfig changed (and this isn't initial mount), refetch orders with current filters
    if (!isInitialMountRef.current) {
      // SEARCH-PERF-001 — skip the refetch when nothing actually changed.
      // The post-search URL sync (updateURLParams) re-triggers this effect
      // via `searchParams`; without this guard every completed search ran
      // the identical query a second time.
      const _sig = JSON.stringify([currentPage, (searchQuery || '').trim(), statusFilter, orderTypeFilter, priorityFilter, String(yearFilter ?? ''), Boolean(unassignedOnly), sortConfig.key, sortConfig.direction, scopeFiltersRef.current.client_code, scopeFiltersRef.current.client_product]);
      if (_sig !== lastFetchSignatureRef.current) {
        fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
      }
    }
    const statsInterval = setInterval(() => {
      if (refreshStats) {
        refreshStats();
      }
    }, 15000);
    const ordersInterval = setInterval(() => {
      // SEARCH-PERF-001 — read CURRENT filter/search values from the live
      // ref (never this effect's possibly-stale closure) so a background
      // tick can never replace active search results with the full list.
      const f = liveFiltersRef.current;
      fetchOrders(f.currentPage, f.searchQuery, f.statusFilter, f.orderTypeFilter, f.priorityFilter, f.yearFilter, f.unassignedOnly, true);
    }, 30000);

    // IDLE-TOAST-SUPPRESS-001 (Jun 2026): when the user rescues an expiring
    // session via "Stay Signed In", refetch immediately instead of waiting
    // for the next 30 s poll tick — any orders/properties requests that
    // 401'd during the idle window recover right away.
    const handleSessionRestored = () => {
      const f = liveFiltersRef.current;
      fetchOrders(f.currentPage, f.searchQuery, f.statusFilter, f.orderTypeFilter, f.priorityFilter, f.yearFilter, f.unassignedOnly, true);
      fetchProperties();
      if (refreshStats) refreshStats();
    };
    window.addEventListener('session-restored', handleSessionRestored);
    
    // Cleanup intervals on component unmount or when dependencies change
    return () => {
      clearInterval(statsInterval);
      clearInterval(ordersInterval);
      window.removeEventListener('session-restored', handleSessionRestored);
    };
  }, [user, currentPage, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly, sortConfig, searchParams]);

  // Handle edit_unlock deep link from notification (separate effect to catch URL changes)
  // NOTE: guarded against duplicate toasts because:
  //   (1) React.StrictMode double-invokes effects in dev,
  //   (2) a re-render while the async fetch is in-flight would otherwise
  //       re-trigger with the query param still present.
  const handledEditUnlockRef = useRef(null);
  useEffect(() => {
    const editUnlockOrderId = searchParams.get('edit_unlock');
    if (!editUnlockOrderId || user?.role !== 'order_desk') return;
    
    // Dedupe: if we've already handled this order id in this session, bail.
    if (handledEditUnlockRef.current === editUnlockOrderId) return;
    handledEditUnlockRef.current = editUnlockOrderId;
    
    // Strip the query param FIRST (synchronous) so any subsequent re-render
    // or StrictMode re-invocation early-returns at the `!editUnlockOrderId`
    // check above, instead of racing another fetch + toast.
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('edit_unlock');
    setSearchParams(newParams, { replace: true });
    
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${editUnlockOrderId}/details`);
        const order = res.data.order || res.data;
        if (order) {
          setSelectedOrder(order);
          setShowOrderDetails(true);
          toast.success('Edit access granted. Make your correction and save.');
        }
      } catch (err) {
        console.error('Failed to load order for edit unlock:', err);
        toast.error('Failed to open order for editing');
      }
    })();
  }, [searchParams, user?.id, user?.role, setSearchParams]);


  // Fetch orders function for Admin/Team Leader dashboard
  // isBackgroundRefresh: true = silent refresh (no loading spinner, prevents flickering)
  //                      false = initial load or user-triggered action (shows loading spinner)
  const fetchOrders = async (page = 1, search = '', status = statusFilter, orderType = orderTypeFilter, priority = priorityFilter, year = yearFilter, unassigned = unassignedOnly, isBackgroundRefresh = false) => {
    // P0 FIX (Feb 27, 2026 — search synchronization):
    // Allocate a unique request ID + AbortController for THIS call, then
    // abort the previous in-flight request. Every setState below is
    // gated on `fetchRequestIdRef.current === myRequestId` so a stale
    // 30-second response can never overwrite a fresher one.
    // SEARCH-PERF-001 — a background 30s tick must NEVER abort an active
    // user-triggered fetch/search (it used to stomp slow searches and
    // silently restore the unfiltered list). Skip this tick entirely; the
    // next one runs with fresh values from liveFiltersRef.
    if (isBackgroundRefresh && userFetchInFlightRef.current) {
      return;
    }
    const myRequestId = ++fetchRequestIdRef.current;
    const myController = new AbortController();
    if (fetchControllerRef.current) {
      try { fetchControllerRef.current.abort(); } catch (_) { /* noop */ }
    }
    fetchControllerRef.current = myController;
    if (!isBackgroundRefresh) {
      userFetchInFlightRef.current = true;
      // Record this fetch's signature so the searchParams-driven effect can
      // skip the duplicate refetch triggered by the post-search URL sync.
      lastFetchSignatureRef.current = JSON.stringify([page, (search || '').trim(), status, orderType, priority, String(year ?? ''), Boolean(unassigned), sortConfig.key, sortConfig.direction, scopeFiltersRef.current.client_code, scopeFiltersRef.current.client_product]);
    }
    
    const _t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    
    try {
      // Only show loading spinner for initial load or user actions, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      const params = {
        page: page,
        limit: ORDERS_PER_PAGE
      };
      
      // Add search query if provided
      const hasSearchTerm = search && search.trim();
      if (hasSearchTerm) {
        params.search = search.trim();
      }
      
      // Add status filter
      // When searching on "All" tab: don't send status → backend decides via
      //   helpers.is_specific_identifier_search: exact Order # / exact Product ID /
      //   structured identifiers include complete+cancelled; general (name/address)
      //   searches exclude them (SEARCH-PID-BROAD-001).
      // When searching on a specific tab: send that status → backend scopes search to that status
      // When not searching on "All" tab: send active statuses only (excludes complete, cancelled)
      
      // Order Desk custom filter tabs (presentation — same params any viewer may send)
      const isOrderDesk = isOrderDeskView;
      if (isOrderDesk && status && status !== 'all') {
        if (status === 'new_today') {
          params.created_today = 'true';
        } else if (status === 'unassigned') {
          params.unassigned_only = 'true';
        } else if (status === 'assigned_today') {
          params.assigned_today = 'true';
        } else if (status === 'completed_entry') {
          params.completed_entry = 'true';
        }
      } else if (status && status.startsWith('mls_')) {
        // MLS-UNIFIED-STATUS-001: unified component-aware bucket (shared
        // backend predicate — same classification as KPI cards + export).
        params.mls_status = status.slice(4);
      } else if (status && status !== 'all') {
        // Specific status tab — always apply, even when searching
        if (status === 'open') {
          params.status = ORDER_STATUS.OPEN;
        } else if (status === 'in_progress') {
          // "Pending" tab includes both in_progress AND pending statuses
          params.status = 'in_progress,pending';
        } else if (status === 'clarification_received') {
          params.status = 'clarification_received';
        } else if (status === 'mailaway_received') {
          params.status = 'mail_away_received';
        } else if (status === 'mailaway') {
          params.status = 'mail_away';
        } else {
          params.status = status;
        }
      } else if (!hasSearchTerm) {
        // "All" tab WITHOUT search — SCOPE-SYNC (Aug 23, 2026): server-side
        // 'active' alias expands to the shared ACTIVE_STATUSES so the table
        // always reconciles exactly with the Total Orders KPI card.
        params.status = 'active';
      }
      // "All" tab WITH search = no status param sent → backend applies the identifier rule above
      
      // Add priority filter
      if (priority && priority !== 'all') {
        params.priority = priority;
      }

      // ORDER-FILTERS-001: TL Client Code / Product filters (reads ref — always fresh)
      if (hasScopeFilters) {
        if (scopeFiltersRef.current.client_code) params.client_code = scopeFiltersRef.current.client_code;
        if (scopeFiltersRef.current.client_product) params.client_product = scopeFiltersRef.current.client_product;
        params.facets = 'true';
      }
      
      // Add order type filter for Admin (tax/search filtering)
      if (nRole(user?.role) === 'admin' && orderType && orderType !== 'all') {
        params.order_type = orderType;
      }

      // MLS-VIEW-SPLIT: Tax TL works in separated Tax / MLS views —
      // 'tax' excludes dual MLS orders from the existing TL scope,
      // 'mls' returns only dual-component orders.
      if (nRole(user?.role) === 'tax_search_team_leader') {
        params.workflow_view = mlsViewRef.current === 'mls' ? 'mls' : 'tax';
      }
      
      // Add year filter (server-side filtering based on received_date or created_at fallback)
      // Skip year filter if "all" is selected to show orders from all years
      if (year && year !== 'all') {
        params.year = year;
      }
      
      // Add unassigned_only filter (for Admin and Team Leaders)
      if (unassigned) {
        params.unassigned_only = 'true';
      }
      
      // Add sorting parameters.
      //
      // May 13, 2026 — Operational aging-visibility (Option A):
      // When the operator narrows the dashboard to a single client (via
      // the search box) AND has not explicitly clicked a column header
      // to set their own sort, we flip the default `created_at DESC`
      // (newest-first) to `created_at ASC` (oldest-first). The intent
      // is to surface aging backlog files at the top automatically so
      // processors don't have to remember to click the Client Code
      // column header every time they filter to a client.
      //
      // Detection rule:
      //   • `hasSearchTerm`  — text in the search box (typical: client
      //                        code like "LTG" / "PNA" / "BANK01"). This
      //                        is the primary trigger your team uses.
      //   • `sortConfig.key` — null/undefined means "no user-clicked
      //                        column sort active" → safe to override
      //                        the default. If the user HAS clicked a
      //                        header, we never override their choice.
      //
      // The unfiltered dashboard view (no search, no manual sort) keeps
      // the newest-first default that powers the at-a-glance "what's
      // happening today" experience.
      const sortParams = getSortParams();
      const userPickedExplicitSort = Boolean(sortConfig.key);
      if (hasSearchTerm && !userPickedExplicitSort) {
        params.sort_by = 'created_at';
        params.sort_order = 'asc';
      } else {
        params.sort_by = sortParams.sort_by;
        params.sort_order = sortParams.sort_order;
      }
      
      // Using v2 API endpoint for paginated orders
      const response = await axios.get(`${API}/orders-v2`, { params, signal: myController.signal, timeout: 30000 });
      
      // P0 FIX (Feb 27, 2026): bail if a newer request superseded this one.
      // Must be the FIRST line after await — never touch state for stale resp.
      if (fetchRequestIdRef.current !== myRequestId) {
        return;
      }
      
      // v2 API always returns paginated response
      if (response.data.orders) {
        const newOrders = response.data.orders;
        setOrders(newOrders);
        setTotalOrders(response.data.total || 0);
        // Admin search-latency badge telemetry (Feb 28, 2026).
        // The backend always populates `search_meta`; we surface it
        // only in the header pill when the current user is an admin.
        if (response.data.search_meta) {
          setSearchMeta(response.data.search_meta);
        }
        // ORDER-FILTERS-001: option counts computed from this very query
        if (response.data.facets) {
          setScopeFacets(response.data.facets);
        }
        
        // P0 FIX #5 (Feb 27, 2026 — selection corruption):
        // Option (c) — preserve selections that still exist in the new
        // result set, drop the ones that fell out, and toast-notify the
        // operator if anything was pruned so they know before clicking
        // a bulk action.
        //
        // IMPORTANT: prune-count MUST be computed SYNCHRONOUSLY here,
        // BEFORE the setState calls. Originally I used updater callbacks
        // `setSelectedOrders(prev => …)` to read prev.size, but React 18
        // schedules updater callbacks for the next commit — meaning the
        // `if ((prunedAdmin + prunedNonAdmin) > 0)` check on the next
        // synchronous line always saw 0 and the toast never fired.
        // Reading the closure-captured `selectedOrders` / `selectedOrderIds`
        // directly avoids the race; the values reflect the state when
        // fetchOrders was invoked, which is correct for pruning decisions.
        const visibleIds = new Set(newOrders.map(o => o.id));
        const keptAdmin = new Set();
        for (const id of selectedOrders) {
          if (visibleIds.has(id)) keptAdmin.add(id);
        }
        const prunedAdmin = selectedOrders.size - keptAdmin.size;
        if (prunedAdmin > 0) setSelectedOrders(keptAdmin);
        
        const keptNonAdmin = new Set();
        for (const id of selectedOrderIds) {
          if (visibleIds.has(id)) keptNonAdmin.add(id);
        }
        const prunedNonAdmin = selectedOrderIds.size - keptNonAdmin.size;
        if (prunedNonAdmin > 0) setSelectedOrderIds(keptNonAdmin);
        
        // Defer the toast to the next tick so React has applied the setState
        // updates. Combined with the request-ID guard above, this only fires
        // for the LATEST request.
        if ((prunedAdmin + prunedNonAdmin) > 0) {
          setTimeout(() => {
            if (fetchRequestIdRef.current === myRequestId) {
              toast.info('Some selections were cleared because they are no longer part of the current search results.');
            }
          }, 0);
        }
      } else {
        // Fallback for unexpected response format
        setOrders([]);
        setTotalOrders(0);
      }
      
      setFetchError(false);
      setCurrentPage(page);
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
      
      // Diagnostic timing (Feb 27, 2026): logs round-trip ms so we can
      // measure perf improvements once index migration #1 lands.
      const _ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - _t0);
      if (_ms > 5000) {
        // Only log slow requests to avoid console noise on healthy traffic.
        // eslint-disable-next-line no-console
        console.warn(`[orders.fetch] slow request: ${_ms}ms search="${search}" page=${page}`);
      }
    } catch (error) {
      // P0 FIX (Feb 27, 2026): silently ignore aborted requests — they were
      // intentionally cancelled by a newer fetch; surfacing a toast would be
      // misleading. axios marks these with code "ERR_CANCELED" and DOMException
      // name "CanceledError" / "AbortError" depending on adapter.
      const isCanceled =
        axios.isCancel?.(error) ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError';
      if (isCanceled) {
        return; // do NOT touch state; the newer request owns it now
      }
      
      // Also bail if a newer request superseded this one between the
      // network failure and this catch (rare but possible).
      if (fetchRequestIdRef.current !== myRequestId) {
        return;
      }
      
      // Only show error toast for user-triggered actions, not background refresh
      if (!isBackgroundRefresh) {
        // SEARCH-PERF-001 — surface a search-specific failure message so a
        // genuinely failed search never looks like "it silently ignored me".
        const _failedTerm = (search || '').trim();
        toast.error(_failedTerm ? `Search failed for "${_failedTerm}". Please try again.` : 'Error fetching orders');
        setFetchError(true);
        setLoading(false);
      }
    } finally {
      // Clear the in-flight flag only if THIS request is still the latest
      // (a superseding request owns the flag lifecycle otherwise).
      if (!isBackgroundRefresh && fetchRequestIdRef.current === myRequestId) {
        userFetchInFlightRef.current = false;
      }
    }
  };

  // Debounced search effect - triggers API call when debounced value changes
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (prevSearchRef.current === searchQuery) return;
    prevSearchRef.current = searchQuery;
    
    const canSearch = ['order_desk', 'admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role)) || isOrderDeskView;
    if (!canSearch) return;

    // PERF-PROD-003: a 1-char term triggers a 22-field regex COLLSCAN on the
    // backend per keystroke. Require 2+ chars (clearing to '' still refetches).
    const trimmed = searchQuery.trim();
    if (trimmed.length === 1) return;

    const runSearch = async () => {
      setIsSearching(true);
      setCurrentPage(1);
      try {
        await fetchOrders(1, searchQuery.trim());
        updateURLParams({ search: searchQuery.trim() });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };
    runSearch();
    // P0 FIX (Feb 27, 2026): debounced searches NEVER emit a success toast.
    // Per ops spec: "only show success toast for explicit Enter/Search button
    // actions". Debounce-triggered fetches are background noise; surfacing a
    // toast for each keystroke was confusing and could show stale values.
  }, [searchQuery]);

  // Search function for explicit Enter key / button search
  const handleSearch = async () => {
    if (!['order_desk', 'admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role))) return;
    
    // P0 FIX (Feb 27, 2026): capture the value at submit time so we can
    // compare against the live input when the slow response resolves.
    // Per spec (a): skip the success toast if the user has changed/cleared
    // the input by the time the request comes back.
    const valueAtSubmit = (searchInputDisplay || '').trim();
    const submitRequestId = fetchRequestIdRef.current + 1; // fetchOrders will increment to this
    
    setIsSearching(true);
    setCurrentPage(1);
    
    try {
      await fetchOrders(1, valueAtSubmit, statusFilter, orderTypeFilter, priorityFilter, yearFilter);
      updateURLParams({ search: valueAtSubmit });
      // Force sync debounced value immediately on explicit search
      setSearchQueryDirect(valueAtSubmit);
      
      // Only fire the success toast if THIS submission is still the active
      // request (not superseded) AND the user hasn't changed the input box.
      const stillActive = fetchRequestIdRef.current === submitRequestId;
      const inputUnchanged = (searchInputDisplay || '').trim() === valueAtSubmit;
      if (valueAtSubmit && stillActive && inputUnchanged) {
        toast.success(`Search completed for: "${valueAtSubmit}"`);
      }
    } catch (error) {
      // Same cancellation guard as fetchOrders — never surface "Search failed"
      // for a request that was intentionally aborted by a newer search.
      const isCanceled =
        axios.isCancel?.(error) ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError';
      if (!isCanceled) {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Clear search and reload all orders.
  //
  // Feb 19, 2026 — flicker fix: we ONLY reset the local search state
  // here. The debounced-search `useEffect` below (keyed on
  // `searchQuery`) is the single source of truth for kicking off the
  // refetch + URL sync. Doing the work in both places caused:
  //   • two back-to-back fetches (cancelled mid-flight),
  //   • two `updateURLParams` calls,
  //   • `isSearching` toggling true → false → true → false rapidly,
  //   • the "Searching…" indicator appearing/disappearing next to the
  //     input — which is the visible flicker the user reported.
  // `clearSearchState()` flips `debouncedValue` to '' synchronously, so
  // the debounced effect picks it up on the very next render.
  const handleClearSearch = () => {
    clearSearchState();
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  // TYPING-TL-ASSIGN-001: on the Typing tab (Title TL + Admin) the Assign control targets
  // Typing Processors and writes typing_* ownership only (Search Processor history untouched).
  const typingAssignMode = statusFilter === 'typing'
    && ['title_search_team_leader', 'admin'].includes(nRole(user?.role));
  // TYPING-PROCESSOR-LIST-001: lightweight roster endpoint; loads on tab entry AND on
  // dropdown open when missing/failed/stale; a failed refresh keeps the last good list.
  const fetchTypingProcessors = useCallback(async () => {
    const res = await axios.get(`${API}/typing/processors`);
    return (res.data?.processors || [])
      .filter(t => t.active !== false)
      .map(t => ({ id: t.user_id, full_name: t.name, name: t.name, email: t.email, role: 'typing_processor', load: t.assigned }));
  }, []);
  const typingRoster = useAssigneeList(fetchTypingProcessors, typingAssignMode);
  const typingProcessors = typingRoster.items;
  const typingListState = useMemo(() => ({
    loading: typingRoster.status === 'loading',
    error: typingRoster.status === 'error',
    onOpen: typingRoster.ensureFresh,
    onRetry: typingRoster.load,
    emptyLabel: 'No active Typing Processors',
  }), [typingRoster.status, typingRoster.ensureFresh, typingRoster.load]);

  const handleTypingBulkAssign = async () => {
    const isAdminRole = nRole(user?.role) === 'admin';
    const selectionSet = isAdminRole ? selectedOrders : selectedOrderIds;
    const setSelectionSet = isAdminRole ? setSelectedOrders : setSelectedOrderIds;
    if (!bulkAssignee || selectionSet.size === 0) {
      toast.error('Please select orders and a Typing Processor');
      return;
    }
    const unassign = bulkAssignee === 'null';
    const target = typingProcessors.find(t => t.id === bulkAssignee);
    setIsBulkAssigning(true);
    let ok = 0; const failures = [];
    for (const orderId of Array.from(selectionSet)) {
      const order = orders.find(o => o.id === orderId);
      const action = unassign ? 'unassign' : (order?.typing_assigned_to ? 'reassign' : 'assign');
      try {
        await axios.post(`${API}/orders/${orderId}/typing/${action}`, unassign ? {} : { user_id: bulkAssignee });
        ok += 1;
      } catch (err) {
        failures.push(`${order?.order_number || orderId}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setIsBulkAssigning(false);
    if (ok) toast.success(unassign ? `${ok} order(s) unassigned in Typing` : `${ok} order(s) assigned to ${target?.full_name || 'Typing Processor'}`);
    if (failures.length) toast.error(`${failures.length} failed: ${failures.slice(0, 3).join(' · ')}`);
    setSelectionSet(new Set());
    setBulkAssignee('');
    fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
  };

  // TYPING-PROCESSOR-LIST-001: regular roster lifecycle (admin /users/processors, TL /processors)
  const [regProcStatus, setRegProcStatus] = useState('idle');
  const regProcStatusRef = useRef('idle');
  regProcStatusRef.current = regProcStatus;
  const regProcLoadedAtRef = useRef(0);

  const fetchProcessors = async () => {
    setRegProcStatus('loading');
    try {
      const response = await axios.get(`${API}/users/processors`);
      setProcessors(response.data);
      regProcLoadedAtRef.current = Date.now();
      setRegProcStatus('ready');
    } catch (error) {
      console.error('Error fetching processors:', error);
      setRegProcStatus('error');
    }
  };

  const handleCreateOrder = async (orderData, overrideDuplicate = false, overrideReason = null, acknowledgeTerminal = false) => {
    try {
      // TITLEFLOW-ORDER-ENTRY-DOCUMENTS-001: files never travel in the JSON body; they are uploaded only after the order exists.
      const { prior_search_files: priorSearchFiles = [], ...orderFields } = orderData || {};
      // Add override/acknowledge flags if bypassing duplicate check
      const requestData = { ...orderFields };
      if (overrideDuplicate) {
        requestData.override_duplicate = true;
        requestData.override_reason = overrideReason;
      }
      if (acknowledgeTerminal) {
        requestData.acknowledge_terminal_duplicate = true;
      }
      
      // Using v2 API endpoint for order creation
      const response = await axios.post(`${API}/orders-v2`, requestData);
      const orderNumber = response.data.order_number || 'N/A';
      
      toast.success(`Order created: ${orderNumber}`, {
        description: 'New order has been successfully created and is ready for processing.',
        duration: 4000
      });

      // Order-first upload: the order already exists (and will not be re-created on retry), so a failed upload can only
      // leave the Prior Search Package missing — never an orphaned file or a duplicate order.
      if (priorSearchFiles.length && response.data.order_id) {
        const { uploaded, failed } = await uploadPriorSearchPackage(axios, API, response.data.order_id, priorSearchFiles);
        if (failed.length) {
          toast.warning(`Order ${orderNumber} created — ${failed.length} of ${priorSearchFiles.length} Prior Search Package file(s) failed to upload`, {
            description: `${failed.map(f => `${f.name}: ${f.reason}`).slice(0, 3).join(' · ')}. Attach them from the order's Files tab.`,
            duration: 10000,
          });
        } else {
          toast.success(`Prior Search Package attached (${uploaded.length} file${uploaded.length === 1 ? '' : 's'})`, { duration: 4000 });
        }
      }
      
      setShowCreateOrder(false);
      setShowDuplicateModal(false);
      setPendingOrderData(null);
      setDuplicateOrder(null);
      createOrderDirtyRef.current = false;
      fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter);
      
      // Refresh KPI statistics
      if (refreshStats) {
        refreshStats();
      }
    } catch (error) {
      // Check if this is a duplicate order error (409 Conflict)
      const conflictType = error.response?.status === 409 ? error.response?.data?.detail?.type : null;
      if (conflictType === 'duplicate_order' || conflictType === 'terminal_duplicate_order') {
        const duplicateInfo = error.response.data.detail;
        setDuplicateOrder(duplicateInfo.existing_order);
        setDuplicateIsTerminal(conflictType === 'terminal_duplicate_order');
        setPendingOrderData(orderData);
        setPendingEditData(null);
        setShowDuplicateModal(true);
        return; // Don't show error toast for duplicates
      }
      if (conflictType === 'duplicate_submit') {
        toast.error('Duplicate submission blocked', {
          description: error.response.data.detail.message,
          duration: 8000,
        });
        return;
      }
      
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response?.data?.detail 
        : 'Failed to create order. Please check all required fields.';
      toast.error('Couldn\'t create order. Please try again.', {
        description: errorMessage,
        duration: 0, // Keep error visible until dismissed
        action: {
          label: 'Retry',
          onClick: () => setShowCreateOrder(true)
        }
      });
    }
  };

  // Handle duplicate modal actions
  const handleViewExistingOrder = (orderId) => {
    setShowDuplicateModal(false);
    setShowCreateOrder(false);
    navigate(`/orders/${orderId}`);
  };

  const handleOverrideDuplicate = async (overrideReason) => {
    if (pendingEditData) {
      const { orderId, updateData } = pendingEditData;
      const retryData = duplicateIsTerminal
        ? { ...updateData, acknowledge_terminal_duplicate: true }
        : { ...updateData, override_duplicate: true, override_reason: overrideReason };
      setShowDuplicateModal(false);
      setPendingEditData(null);
      setDuplicateOrder(null);
      await handleUpdateOrder(orderId, retryData);
      return;
    }
    if (pendingOrderData) {
      if (duplicateIsTerminal) {
        await handleCreateOrder(pendingOrderData, false, null, true);
      } else {
        await handleCreateOrder(pendingOrderData, true, overrideReason);
      }
    }
  };

  const handleCreateCustomer = async (customerData) => {
    try {
      const response = await axios.post(`${API}/customers`, customerData);
      const customerName = response.data.name || 'Customer';
      
      toast.success(`Customer created: ${customerName}`, {
        description: 'New customer has been successfully added to the system.',
        duration: 4000
      });
      
      setShowCreateCustomer(false);
      fetchCustomers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create customer. Please check all required fields.';
      toast.error('Couldn\'t create customer. Please try again.', {
        description: errorMessage,
        duration: 0, // Keep error visible until dismissed
        action: {
          label: 'Retry',
          onClick: () => setShowCreateCustomer(true)
        }
      });
    }
  };



  const handleUpdateOrder = async (orderId, updateData) => {
    // Find the current order in state for optimistic update
    const currentOrder = orders.find(o => o.id === orderId);
    const previousOrders = [...orders];
    
    // ========================================
    // GLOBAL STATUS UPDATE RULE: Optimistic UI
    // ========================================
    // Immediately update the order in local state
    // This provides instant feedback to the user
    if (updateData.status && currentOrder) {
      // Optimistic update - immediately reflect the change
      setOrders(prevOrders => prevOrders.map(order => 
        order.id === orderId 
          ? { ...order, status: updateData.status, _updating: true }
          : order
      ));
    }
    
    try {
      // Using v2 API endpoint for order updates
      await axios.put(`${API}/orders-v2/${orderId}`, updateData);
      
      toast.success('Order updated successfully', {
        description: 'All changes have been saved'
      });
      
      // ========================================
      // PERFORMANCE FIX: No full table reload on success
      // ========================================
      // The optimistic update (above) already reflects the change in the UI.
      // Only refresh KPI stats — background polling handles eventual consistency.
      if (refreshStats) {
        refreshStats();
      }
      
      // Clear edit unlock fields from local state after save
      // (the backend consumes the unlock on successful save)
      setOrders(prevOrders => prevOrders.map(o => 
        o.id === orderId 
          ? { ...o, edit_unlocked_for: null, edit_unlock_expires_at: null, edit_unlock_request_id: null, edit_unlock_field: null }
          : o
      ));
      
      return true;
    } catch (error) {
      console.error('Update order error:', error);
      
      // ========================================
      // GLOBAL STATUS UPDATE RULE: Rollback on Failure
      // ========================================
      // Restore previous state if API call failed
      setOrders(previousOrders);

      // DUP-UNIFY-001: edit collided with another order → same modal flow.
      const conflictType = error.response?.status === 409 ? error.response?.data?.detail?.type : null;
      if (conflictType === 'duplicate_order' || conflictType === 'terminal_duplicate_order') {
        const duplicateInfo = error.response.data.detail;
        setDuplicateOrder(duplicateInfo.existing_order);
        setDuplicateIsTerminal(conflictType === 'terminal_duplicate_order');
        setPendingEditData({ orderId, updateData });
        setPendingOrderData(null);
        setShowDuplicateModal(true);
        return false;
      }
      
      const errorMessage = error.response?.data?.detail || 'Couldn\'t save changes. Please try again.';
      toast.error('Couldn\'t save changes', {
        description: errorMessage
      });
      return false;
    }
  };

  // Handle inline status change (Admin/Team Leader only)
  const handleInlineStatusChange = async (orderId, newStatus, currentStatus) => {
    if (newStatus === currentStatus) return;
    
    // ========================================
    // GLOBAL STATUS UPDATE RULE: Optimistic UI for inline change
    // ========================================
    const previousOrders = [...orders];
    setOrders(prevOrders => prevOrders.map(order => 
      order.id === orderId 
        ? { ...order, status: newStatus, _updating: true }
        : order
    ));
    
    const success = await handleUpdateOrder(orderId, { status: newStatus });
    if (success) {
      // GLOBAL RULE: Redirect to dashboard after ANY successful status update
      // Applies to: Admin, Team Leader
      // This prevents stale UI, duplicate actions, and ensures fresh dashboard data
      const rolesWithDashboardRedirect = [
        'admin', 
        'team_leader', 
        'tax_search_team_leader', 
        'title_search_team_leader'
      ];
      
      const userRole = user?.role?.toLowerCase() || '';
      const shouldRedirect = rolesWithDashboardRedirect.includes(userRole);
      
      if (shouldRedirect) {
        toast.success(`Order status updated to ${newStatus}. Redirecting to dashboard...`, { duration: 1500 });
        console.log(`[ORDERS] Status changed to ${newStatus}. Redirecting to dashboard in 1.5 seconds...`);
        setTimeout(() => {
          console.log('[ORDERS] Executing redirect to dashboard NOW');
          navigate('/dashboard');
        }, 1500);
      }
    }
    // Rollback already handled in handleUpdateOrder if !success
  };

  // Handle inline assignee change (Admin only)
  // Unified assignment API call - used by both inline and bulk assignment
  const assignOrder = async (orderId, assigneeId, currentVersion = null) => {
    try {
      // Using v2 API endpoint for assignment
      const response = await axios.post(`${API}/orders-v2/${orderId}/assign-v2`, {
        assignee_id: assigneeId,
        version: currentVersion
      });
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response?.status === 409) {
        // Concurrency conflict
        return { success: false, error: '409', message: 'Order was updated. Please refresh.' };
      } else if (error.response?.status === 422) {
        return { success: false, error: '422', message: error.response.data.detail || 'Invalid operation' };
      } else {
        return { success: false, error: 'unknown', message: error.response?.data?.detail || 'Assignment failed' };
      }
    }
  };
  
  // Unified bulk assignment API call
  const bulkAssignOrders = async (orderIds, assigneeId) => {
    try {
      // Using v2 API endpoint for bulk assignment
      const response = await axios.post(`${API}/orders-v2/bulk-assign-v2`, {
        order_ids: orderIds,
        assignee_id: assigneeId
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Bulk assignment failed' };
    }
  };
  
  // Handle inline assignee change using new unified API
  const handleInlineAssigneeChange = async (orderId, newAssignee, currentAssignee) => {
    if (newAssignee === currentAssignee) return;
    
    // ========================================
    // OPTIMISTIC UI: Immediate visual feedback
    // ========================================
    const previousOrders = [...orders];
    setOrders(prevOrders => prevOrders.map(order => 
      order.id === orderId 
        ? { ...order, assignee_id: newAssignee, _updating: true }
        : order
    ));
    
    const result = await assignOrder(orderId, newAssignee);
    
    if (result.success) {
      const data = result.data;
      // Show appropriate toast based on response
      const assigneeName = data.assignee_name || data.assignee?.full_name;
      if (assigneeName && currentAssignee) {
        toast.success(`Reassigned to ${assigneeName}`);
      } else if (assigneeName) {
        toast.success(`Assigned to ${assigneeName}`);
      } else {
        toast.success('Unassigned successfully');
      }
      
      // ========================================
      // INSTANT UPDATE: Use API response directly (no fetchOrders)
      // This eliminates flicker caused by stale data in re-fetch
      // V1 WORKFLOW: MUST sync status from backend (reassignment resets to "open")
      // ========================================
      setOrders(prevOrders => prevOrders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              assignee_id: data.assignee_id,
              assignee_name: data.assignee_name || assigneeName,
              assigned_at: data.assigned_at,
              status: data.status || order.status, // V1 WORKFLOW: Sync status from backend
              _updating: false 
            }
          : order
      ));
      
      // Only refresh stats for KPI counts (not full orders list)
      // Background polling will handle eventual consistency
      if (refreshStats) {
        refreshStats();
      }
    } else if (result.error === '409') {
      // Concurrency conflict - rollback and force refresh
      setOrders(previousOrders);
      toast.error(result.message);
      fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter);
    } else {
      // Rollback on failure
      setOrders(previousOrders);
      toast.error(result.message);
    }
  };

  // Admin-specific selection handlers (with persistence across pagination)
  const handleAdminSelectAll = (checked) => {
    const newSelection = new Set(selectedOrders);
    if (checked) {
      // Add all orders on current page to selection
      orders.forEach(o => newSelection.add(o.id));
    } else {
      // Remove all orders on current page from selection
      orders.forEach(o => newSelection.delete(o.id));
    }
    setSelectedOrders(newSelection);
  };

  const handleAdminSelectOrder = (orderId, checked) => {
    const newSelection = new Set(selectedOrders);
    if (checked) {
      newSelection.add(orderId);
    } else {
      newSelection.delete(orderId);
    }
    setSelectedOrders(newSelection);
  };
  
  // Clear all selections manually
  const handleClearSelection = () => {
    setSelectedOrders(new Set());
    setBulkAssignee('');
    toast.info('Selection cleared');
  };

  // MLS-VIEW-SPLIT: switching views clears selections + assignment controls
  // so order IDs / dropdown state never carry between the two modes.
  const handleMlsViewSwitch = (view) => {
    if (view === mlsView) return;
    setMlsView(view);
    mlsViewRef.current = view;
    setSelectedOrders(new Set());
    setSelectedOrderIds(new Set());
    setBulkAssignee('');
    // ORDER-FILTERS-001: product options differ per view — reset scope filters
    scopeFiltersRef.current = { client_code: '', client_product: '' };
    setScopeFilters({ client_code: '', client_product: '' });
    setCurrentPage(1);
    // MLS-UNIFIED-STATUS-001: each view has its own tab set → back to All;
    // KPI cards re-read the `view` URL param so they follow the switch.
    setStatusFilter('all');
    updateURLParams({ view, status: 'all', client_code: '', product: '' });
    fetchOrders(1, searchQuery, 'all', orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
    if (refreshStats) setTimeout(() => refreshStats(), 0);
  };

  // Handle bulk assignment - Direct assignment without confirmation modal
  // Uses toast notifications for feedback instead of modal popups
  const handleAdminBulkAssign = async () => {
    // Determine which selection state to use based on role
    const isAdmin = nRole(user?.role) === 'admin';
    const selectionSet = isAdmin ? selectedOrders : selectedOrderIds;
    const setSelectionSet = isAdmin ? setSelectedOrders : setSelectedOrderIds;
    const processorList = isAdmin ? processors : availableProcessors;
    
    if (!bulkAssignee || selectionSet.size === 0) {
      toast.error('Please select orders and an assignee');
      return;
    }

    // Handle "null" string for Unassigned
    const assigneeId = bulkAssignee === 'null' ? null : bulkAssignee;
    
    let assignee = assigneeId ? processorList.find(p => p.id === assigneeId) : null;
    // Check if assignee is the current user (self-assignment via dropdown)
    if (!assignee && assigneeId === user?.id) {
      assignee = { id: user.id, full_name: user.full_name || user.name || user.email };
    }
    const assigneeName = assignee ? normalizeProcessor(assignee).label : 'Unassigned';
    
    const orderIdsToAssignAll = Array.from(selectionSet);
    // MLS-ASSIGN-3B: dual MLS orders are never parent-assigned — they are
    // handled by the MLS component controls (Apply) in the same toolbar, so
    // they are silently excluded here.
    const mlsSplit = splitDualMlsSelection(orderIdsToAssignAll, orders);
    if (mlsSplit.rest.length === 0) return;
    const orderIdsToAssign = mlsSplit.rest;
    const selectedCount = orderIdsToAssign.length;
    
    // ========================================
    // OPTIONAL SAFETY: Show confirmation only for large batches or unassignment
    // Uncomment if you want confirmation for >= 25 orders or unassignment
    // ========================================
    // const requiresConfirmation = selectedCount >= 25 || assigneeId === null;
    // if (requiresConfirmation) {
    //   setAssignModalData({ orderCount: selectedCount, assigneeName, assigneeId });
    //   setAssignModalState('confirm');
    //   setShowAssignModal(true);
    //   return;
    // }
    
    // ========================================
    // DIRECT ASSIGNMENT: No confirmation modal
    // ========================================
    setIsBulkAssigning(true);
    
    const result = await bulkAssignOrders(orderIdsToAssign, assigneeId);
    
    setIsBulkAssigning(false);
    
    if (result.success) {
      // API returns: { success_count, failure_count, failures, message }
      const data = result.data || {};
      const successCount = data.success_count ?? 0;
      const failCount = data.failure_count ?? 0;
      const failures = data.failures ?? [];
      const failedOrderIds = failures.map(f => f.order_id);
      
      // ========================================
      // INSTANT UPDATE: Update successful orders locally (no fetchOrders)
      // V1 WORKFLOW: Reassignment resets status to "open" for new assignee
      // ========================================
      if (successCount > 0) {
        const now = new Date().toISOString();
        setOrders(prevOrders => prevOrders.map(order => {
          if (orderIdsToAssign.includes(order.id) && !failedOrderIds.includes(order.id)) {
            // V1 WORKFLOW RULE: If reassigning to a different user, status resets to "open"
            // Backend handles this, but we sync frontend for instant feedback
            const currentAssigneeId = order.assignee_id;
            const isReassignment = currentAssigneeId && currentAssigneeId !== assigneeId;
            const isUnassign = assigneeId === null;
            
            // Reset status to "open" if:
            // 1. It's a reassignment (different assignee), OR
            // 2. It's an unassignment AND status is an active processing status
            const activeStatuses = ['pending', 'in_progress', 'clarification', 'clarification_received', 
                                    'mail_away', 'mail_away_received', 'qc_review', 'processing'];
            const shouldResetStatus = isReassignment || (isUnassign && activeStatuses.includes(order.status?.toLowerCase()));
            
            return {
              ...order,
              assignee_id: assigneeId,
              assignee_name: assigneeName !== 'Unassigned' ? assigneeName : null,
              assigned_at: now,
              status: shouldResetStatus ? 'open' : order.status, // V1 WORKFLOW: Reset status
              _updating: false
            };
          }
          return order;
        }));
      }
      
      // ========================================
      // TOAST NOTIFICATIONS ONLY (no modals)
      // ========================================
      if (successCount === 0 && failCount > 0) {
        // ALL FAILED
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.error(`Assignment blocked`, {
          description: failureMessages || `${failCount} order(s) could not be assigned`,
          duration: 6000
        });
      } else if (successCount > 0 && failCount > 0) {
        // PARTIAL SUCCESS
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.warning(`Partial: ${successCount} assigned, ${failCount} failed`, {
          description: failureMessages,
          duration: 6000
        });
      } else if (assigneeId === null) {
        // UNASSIGN SUCCESS
        toast.success(`Unassigned ${successCount} order${successCount !== 1 ? 's' : ''}`);
      } else {
        // ASSIGN SUCCESS
        toast.success(`Assigned ${successCount} order${successCount !== 1 ? 's' : ''} to ${assigneeName}`);
      }
    } else {
      // API error
      const errorMessage = result.message || 'Assignment failed';
      toast.error(errorMessage);
    }
    
    // Refresh stats for KPI counts
    if (refreshStats) {
      refreshStats();
    }
    
    // Clear selection
    setSelectionSet(new Set());
    setBulkAssignee('');
  };

  // "Assign to Me" handler - assigns selected orders directly to the current user
  const handleAssignToMe = async (opts = {}) => {
    // TL-SELF-ASSIGN-001: `quietDual` — called from the MLS Assign-to-Me menu, which
    // handles the dual orders itself; only the dual-skip toast is suppressed.
    const quietDual = opts?.quietDual === true;
    const isAdmin = nRole(user?.role) === 'admin';
    const selectionSet = isAdmin ? selectedOrders : selectedOrderIds;
    const setSelectionSet = isAdmin ? setSelectedOrders : setSelectedOrderIds;

    if (selectionSet.size === 0) {
      toast.error('Please select orders to assign');
      return;
    }

    const orderIdsToAssignRaw = Array.from(selectionSet);
    // MLS-ASSIGN-3B: dual MLS orders cannot be self-assigned at parent level.
    const meSplit = splitDualMlsSelection(orderIdsToAssignRaw, orders);
    if (meSplit.dual.length > 0) {
      if (!quietDual) toast.warning(`${meSplit.dual.length} dual MLS order${meSplit.dual.length !== 1 ? 's' : ''} skipped`, {
        description: 'Bulk assignment is not available for dual MLS orders. Assign Tax Search and MLS Search components individually.',
        duration: 7000,
      });
      if (meSplit.rest.length === 0) return;
    }
    const orderIdsToAssign = meSplit.rest;
    const assigneeName = user?.full_name || user?.name || user?.email || 'Me';

    setIsBulkAssigning(true);
    const result = await bulkAssignOrders(orderIdsToAssign, user.id);
    setIsBulkAssigning(false);

    if (result.success) {
      const data = result.data || {};
      const successCount = data.success_count ?? 0;
      const failCount = data.failure_count ?? 0;
      const failures = data.failures ?? [];
      const failedOrderIds = failures.map(f => f.order_id);

      if (successCount > 0) {
        const now = new Date().toISOString();
        setOrders(prevOrders => prevOrders.map(order => {
          if (orderIdsToAssign.includes(order.id) && !failedOrderIds.includes(order.id)) {
            const currentAssigneeId = order.assignee_id;
            const isReassignment = currentAssigneeId && currentAssigneeId !== user.id;
            const activeStatuses = ['pending', 'in_progress', 'clarification', 'clarification_received',
                                    'mail_away', 'mail_away_received', 'qc_review', 'processing'];
            const shouldResetStatus = isReassignment;
            return {
              ...order,
              assignee_id: user.id,
              assignee_name: assigneeName,
              assigned_at: now,
              status: shouldResetStatus ? 'open' : order.status,
              _updating: false
            };
          }
          return order;
        }));
      }

      if (successCount === 0 && failCount > 0) {
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.error(`Assignment blocked`, { description: failureMessages || `${failCount} order(s) could not be assigned`, duration: 6000 });
      } else if (successCount > 0 && failCount > 0) {
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.warning(`Partial: ${successCount} assigned to you, ${failCount} failed`, { description: failureMessages, duration: 6000 });
      } else {
        toast.success(`Assigned ${successCount} order${successCount !== 1 ? 's' : ''} to yourself`);
      }
    } else {
      toast.error(result.message || 'Assignment failed');
    }

    if (refreshStats) refreshStats();
    setSelectionSet(new Set());
    setBulkAssignee('');
  };

  // Execute assignment from modal (only used if confirmation is enabled above)
  const executeAdminBulkAssign = async () => {
    const { orderCount, assigneeId, assigneeName } = assignModalData;
    
    const isAdmin = nRole(user?.role) === 'admin';
    const selectionSet = isAdmin ? selectedOrders : selectedOrderIds;
    const setSelectionSet = isAdmin ? setSelectedOrders : setSelectedOrderIds;
    const orderIdsToAssign = Array.from(selectionSet);
    
    setShowAssignModal(false); // Close modal immediately
    setIsBulkAssigning(true);
    
    const result = await bulkAssignOrders(orderIdsToAssign, assigneeId);
    
    setIsBulkAssigning(false);
    
    if (result.success) {
      const data = result.data || {};
      const successCount = data.success_count ?? 0;
      const failCount = data.failure_count ?? 0;
      const failures = data.failures ?? [];
      const failedOrderIds = failures.map(f => f.order_id);
      
      if (successCount > 0) {
        const now = new Date().toISOString();
        setOrders(prevOrders => prevOrders.map(order => {
          if (orderIdsToAssign.includes(order.id) && !failedOrderIds.includes(order.id)) {
            // V1 WORKFLOW RULE: If reassigning to a different user, status resets to "open"
            const currentAssigneeId = order.assignee_id;
            const isReassignment = currentAssigneeId && currentAssigneeId !== assigneeId;
            const isUnassign = assigneeId === null;
            
            const activeStatuses = ['pending', 'in_progress', 'clarification', 'clarification_received', 
                                    'mail_away', 'mail_away_received', 'qc_review', 'processing'];
            const shouldResetStatus = isReassignment || (isUnassign && activeStatuses.includes(order.status?.toLowerCase()));
            
            return {
              ...order,
              assignee_id: assigneeId,
              assignee_name: assigneeName !== 'Unassigned' ? assigneeName : null,
              assigned_at: now,
              status: shouldResetStatus ? 'open' : order.status, // V1 WORKFLOW: Reset status
              _updating: false
            };
          }
          return order;
        }));
      }
      
      // Toast notifications only
      if (successCount === 0 && failCount > 0) {
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.error(`Assignment blocked`, { description: failureMessages, duration: 6000 });
      } else if (successCount > 0 && failCount > 0) {
        const failureMessages = failures.slice(0, 2).map(f => f.error || f.message).join('; ');
        toast.warning(`Partial: ${successCount} assigned, ${failCount} failed`, { description: failureMessages, duration: 6000 });
      } else if (assigneeId === null) {
        toast.success(`Unassigned ${successCount} order${successCount !== 1 ? 's' : ''}`);
      } else {
        toast.success(`Assigned ${successCount} order${successCount !== 1 ? 's' : ''} to ${assigneeName}`);
      }
    } else {
      toast.error(result.message || 'Assignment failed');
    }
    
    if (refreshStats) refreshStats();
    setSelectionSet(new Set());
    setBulkAssignee('');
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'created': return 'secondary';
      case 'open': return 'default';
      case 'pending': return 'outline';
      case 'clarification': return 'destructive';
      case 'mailaway': return 'outline';
      case 'mail_away': return 'outline';
      case 'mail_away_received': return 'default';  // Distinct style for received
      case 'complete': return 'default';
      case 'processing': return 'default';
      case 'completed': return 'default';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getBorrowerName = (order) => {
    // Use fallback logic: borrower_name (primary) → customer_name (fallback) → 'N/A'
    return order.borrower_name || order.customer_name || 'N/A';
  };

  // getPropertyAddress function moved to global scope as formatPropertyAddress

  const getPropertyCounty = (order) => formatCounty(order.property_county || order.county);
  const getPropertyState  = (order) => formatState(order.property_state || order.state);

  const getDensityClasses = () => {
    switch (tableDensity) {
      case 'compact':
        return 'h-8'; // Compact row height
      case 'comfortable':
        return 'h-14'; // Comfortable row height
      default:
        return 'h-12'; // Normal row height
    }
  };

  const getCellDensityClasses = () => {
    switch (tableDensity) {
      case 'compact':
        return 'py-1 px-2 text-sm'; // Compact padding and font
      case 'comfortable':
        return 'py-4 px-4'; // Comfortable padding
      default:
        return 'py-2 px-3'; // Normal padding
    }
  };

  // Assignment functions with processor caching
  const fetchAvailableProcessors = async (forceRefresh = false) => {
    const now = Date.now();
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
    
    // Use cached data if available and not expired (unless forced refresh)
    if (!forceRefresh && processorCacheTimestamp && availableProcessors.length > 0 && (now - processorCacheTimestamp) < cacheExpiry) {
      console.log('Using cached processors:', availableProcessors); // Debug log
      return;
    }
    
    try {
      setRegProcStatus('loading');
      const response = await axios.get(`${API}/processors`);
      const processors = response.data;
      
      // Filter processors based on team leader role
      // ASSIGNEE-MULTIROLE-001: prefer the server's `assignable_workstreams` (covers QC Specialist + Processor
      // multi-role users whose primary role is not a processor); fall back to the role key for legacy rows.
      const normalizeRole = (r) => (r || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
      const hasWs = (p, ws, roleKeys) => Array.isArray(p.assignable_workstreams)
        ? p.assignable_workstreams.includes(ws)
        : roleKeys.includes(normalizeRole(p.role));
      const isTaxProc = (p) => hasWs(p, 'tax_search', ['processor', 'tax_processor']);
      const isSearchProc = (p) => hasWs(p, 'title_search', ['search_processor', 'title_search_processor']);

      let filteredProcessors = [];
      const role = nRole(user?.role);
      if (role === 'title_search_team_leader') {
        filteredProcessors = processors.filter(isSearchProc);
      } else if (role === 'tax_search_team_leader' || role === 'team_leader') {
        filteredProcessors = processors.filter(isTaxProc);
      } else {
        filteredProcessors = processors; // Order desk sees all
      }
      
      // Update cache with new data
      setAvailableProcessors([...filteredProcessors]);
      setProcessorCacheTimestamp(now);
      regProcLoadedAtRef.current = now;
      setRegProcStatus('ready');
      console.log('Processors fetched and cached:', filteredProcessors); // Debug log
    } catch (error) {
      console.error('Error fetching processors:', error);
      setRegProcStatus('error');
      toast.error('Failed to load processors');
    }
  };

  // TYPING-PROCESSOR-LIST-001: dropdown-open hook for the regular roster — reload when
  // idle/failed or stale (>5 min); a failed refresh keeps the last good list.
  const regularRosterIsAdmin = nRole(user?.role) === 'admin';
  const ensureRegularProcessorsFresh = useCallback(() => {
    const s = regProcStatusRef.current;
    if (s === 'loading') return;
    if (s !== 'ready' || Date.now() - regProcLoadedAtRef.current > ASSIGNEE_LIST_STALE_MS) {
      if (regularRosterIsAdmin) fetchProcessors(); else fetchAvailableProcessors(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRosterIsAdmin]);
  const retryRegularProcessors = useCallback(() => {
    if (regularRosterIsAdmin) fetchProcessors(); else fetchAvailableProcessors(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRosterIsAdmin]);
  const processorListState = useMemo(() => ({
    loading: regProcStatus === 'loading',
    error: regProcStatus === 'error',
    onOpen: ensureRegularProcessorsFresh,
    onRetry: retryRegularProcessors,
    emptyLabel: 'No active processors',
  }), [regProcStatus, ensureRegularProcessorsFresh, retryRegularProcessors]);

  const handleOrderNumberClick = (order) => {
    // Order Desk can ONLY edit orders that are NOT assigned
    if (nRole(user?.role) === 'order_desk') {
      const isAssigned = order.assignee_id;
      // Check if order is in a mid-workflow protected status
      const protectedStatuses = ['mail_away_received', 'clarification', 'clarification_received'];
      const isProtectedStatus = protectedStatuses.includes(order.status);
      
      // Check if this order has an approved edit unlock for this user
      const hasEditUnlock = order.edit_unlocked_for === user?.id && order.edit_unlock_expires_at && new Date(order.edit_unlock_expires_at) > new Date();
      
      if (hasEditUnlock) {
        // Approved edit request — open editable form directly
        toast.success('Edit access granted. Make your correction and save.');
        setSelectedOrder(order);
        setShowOrderDetails(true);
        return;
      }
      
      if (isAssigned || isProtectedStatus) {
        // Order is assigned OR in protected status - show read-only Order Summary with "Request Edit" button
        const reason = isAssigned
          ? 'This order has been assigned. You can request edits if needed.'
          : `This order is in '${order.status?.replace(/_/g, ' ')}' status. You can request edits if needed.`;
        toast.info(reason);
        setOrderSummaryPopup({ 
          isOpen: true, 
          orderData: order, 
          showRequestEditButton: true,
          onRequestEdit: () => {
            setOrderSummaryPopup({ isOpen: false, orderData: null });
            setEditRequestModal({ isOpen: true, orderData: order });
          }
        });
        return;
      }
      
      // Order is NOT assigned - allow editing
      setSelectedOrder(order);
      setShowOrderDetails(true);
      return;
    }

    // Feb 2026 — DELIVERY-EDIT-RBAC-001 (revised) — Default behavior for
    // Admin and Team Leaders: open the read-only Order Details popup
    // (Summary/Overview/Fulfillment/Activity/Files tabs). The popup
    // surfaces an explicit "Edit Order" button when the user has edit
    // permission for the order, which is the ONLY entry point into the
    // editable modal. Out-of-team TLs see the popup WITHOUT the Edit
    // Order button.
    const roleKey = nRole(user?.role);
    const orderIsTax = isTaxProduct(order);
    let canEditOrder = false;
    if (roleKey === 'admin' || roleKey === 'team_leader') {
      canEditOrder = true;
    } else if (roleKey === 'tax_search_team_leader') {
      canEditOrder = orderIsTax;
    } else if (roleKey === 'title_search_team_leader') {
      canEditOrder = !orderIsTax;
    } else if (canEditOrdersViaMatrix) {
      canEditOrder = true; // RBAC-PILOT-ORDERS-001: field edits via matrix grant
    }

    setOrderSummaryPopup({
      isOpen: true,
      orderData: order,
      showEditOrderButton: canEditOrder,
      onEditOrder: canEditOrder ? () => {
        setOrderSummaryPopup({ isOpen: false, orderData: null });
        setSelectedOrder(order);
        setShowOrderDetails(true);
      } : undefined,
    });
  };

  // Bulk selection functions
  const handleSelectOrder = (orderId, isSelected) => {
    const newSelected = new Set(selectedOrderIds);
    if (isSelected) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrderIds(newSelected);
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allOrderIds = orders.map(order => order.id);
      setSelectedOrderIds(new Set(allOrderIds));
    } else {
      setSelectedOrderIds(new Set());
    }
  };

  const handleBulkAssign = async () => {
    if (selectedOrderIds.size === 0) {
      toast.error('Please select at least one order');
      return;
    }

    // MLS-ASSIGN-3B: dual MLS orders use the component controls — excluded here.
    const tlSelectionAll = Array.from(selectedOrderIds);
    const tlSplit = splitDualMlsSelection(tlSelectionAll, orders);
    if (tlSplit.dual.length > 0) {
      toast.warning(`${tlSplit.dual.length} dual MLS order${tlSplit.dual.length !== 1 ? 's' : ''} skipped`, {
        description: 'Use the MLS T / M component controls to assign dual MLS orders.',
        duration: 7000,
      });
      if (tlSplit.rest.length === 0) return;
    }

    setIsBulkAssigning(true);
    const selectedOrdersArray = tlSplit.rest;
    const assigneeId = bulkAssignProcessor === 'none' ? null : bulkAssignProcessor;

    try {
      // Use the v2 bulk assignment API endpoint
      const response = await axios.post(`${API}/orders-v2/bulk-assign-v2`, {
        order_ids: selectedOrdersArray,
        assignee_id: assigneeId
      });

      const result = response.data || {};
      // API returns: { success_count, failure_count, failures, message }
      const successCount = result.success_count ?? 0;
      const failCount = result.failure_count ?? 0;
      const failures = result.failures ?? [];
      const failedOrderIds = failures.map(f => f.order_id);
      
      // Get processor name for success message
      const processorName = bulkAssignProcessor !== 'none' 
        ? (availableProcessors.find(p => p.id === bulkAssignProcessor)?.full_name || 'processor')
        : null;
      
      // ========================================
      // INSTANT UPDATE: Update successful orders locally (no fetchOrders)
      // V1 WORKFLOW: Reassignment resets status to "open" for new assignee
      // ========================================
      if (successCount > 0) {
        const now = new Date().toISOString();
        setOrders(prevOrders => prevOrders.map(order => {
          // Only update orders that were selected AND not in failures
          if (selectedOrdersArray.includes(order.id) && !failedOrderIds.includes(order.id)) {
            // V1 WORKFLOW RULE: If reassigning to a different user, status resets to "open"
            const currentAssigneeId = order.assignee_id;
            const isReassignment = currentAssigneeId && currentAssigneeId !== assigneeId;
            const isUnassign = assigneeId === null;
            
            const activeStatuses = ['pending', 'in_progress', 'clarification', 'clarification_received', 
                                    'mail_away', 'mail_away_received', 'qc_review', 'processing'];
            const shouldResetStatus = isReassignment || (isUnassign && activeStatuses.includes(order.status?.toLowerCase()));
            
            return {
              ...order,
              assignee_id: assigneeId,
              assignee_name: processorName,
              assigned_at: now,
              status: shouldResetStatus ? 'open' : order.status, // V1 WORKFLOW: Reset status
              _updating: false
            };
          }
          return order;
        }));
      }
      
      // Show success/error feedback based on API response
      if (failCount === 0) {
        // All succeeded
        if (bulkAssignProcessor === 'none') {
          toast.success(`Orders unassigned successfully!`, {
            description: `${successCount} order${successCount !== 1 ? 's' : ''} set to Open`
          });
        } else {
          toast.success(`Orders assigned successfully!`, {
            description: `${successCount} order${successCount !== 1 ? 's' : ''} assigned to ${processorName}`
          });
        }
      } else if (successCount > 0) {
        // Partial success
        const action = bulkAssignProcessor === 'none' ? 'unassigned' : 'assigned';
        const target = bulkAssignProcessor === 'none' ? '' : ` to ${processorName}`;
        const failureMessages = failures.map(f => f.error || f.message).join('; ');
        
        toast.warning(`Partial success`, {
          description: `${successCount} order${successCount !== 1 ? 's' : ''} ${action}${target}. ${failCount} failed: ${failureMessages}`
        });
      } else {
        // All failed
        const failureMessage = failures.length > 0 ? (failures[0]?.error || failures[0]?.message || '') : '';
        toast.error(`Assignment not allowed`, {
          description: failureMessage || `${failCount} order${failCount !== 1 ? 's' : ''} could not be assigned`
        });
      }

      // Clear selection and close modal
      setSelectedOrderIds(new Set());
      setBulkAssignProcessor('');
      setShowBulkAssignModal(false);

      // Only refresh stats for KPI counts (not full orders list)
      // Background polling handles eventual consistency
      if (refreshStats) {
        refreshStats();
      }

    } catch (error) {
      console.error('Bulk assignment error:', error);
      
      // Show error details if available - handle different error response formats
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (errorDetail?.msg || error.response?.data?.message || 'Please try again or contact support');
      
      toast.error('Assignment not allowed', {
        description: errorMessage
      });
    } finally {
      setIsBulkAssigning(false);
    }
  };

  // Individual assignment function removed - using bulk assignment instead

  // Fetch processors when component mounts and user role is team leader
  useEffect(() => {
    const role = nRole(user?.role);
    if (role === 'title_search_team_leader' || role === 'tax_search_team_leader' || role === 'team_leader') {
      fetchAvailableProcessors();
    }
  }, [user?.role]);

  // ORDER-FILTERS-001: search row (Search | Client Code | Product). For TLs it is rendered
  // inside the toolbar row so Reports/Assign sit on the same line as in the approved design.
  const canUseSearchRow = ['admin', 'title_search_team_leader', 'tax_search_team_leader', 'team_leader'].includes(nRole(user?.role));
  const searchRow = canUseSearchRow ? (
    <div className={hasScopeFilters ? 'flex flex-1 flex-wrap gap-2 items-center min-w-0' : 'flex gap-2 items-center'}>
      <div className="relative" style={{ flex: '1 1 auto', minWidth: hasScopeFilters ? '160px' : '200px', maxWidth: '400px' }}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by Order #, Borrower, Address, Client Code, Assignee..."
          value={searchInputDisplay}
          onChange={(e) => setSearchInputDisplay(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          className={hasScopeFilters
            ? 'h-9 pl-10 pr-10 w-full rounded-lg border-slate-200 bg-white text-sm shadow-sm placeholder:text-slate-400 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-300'
            : 'pl-10 pr-10 w-full'}
          data-testid="orders-search-input"
        />
        {/* Clear button inside input.
            Feb 19, 2026 — `onMouseDown` (not `onClick`) so the click
            fires BEFORE the input's blur event, preventing the
            input from briefly losing focus and re-painting. */}
        {searchInputDisplay && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              handleClearSearch();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Clear search"
            data-testid="orders-search-clear-btn"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {/* Fixed-width spinner slot — reserves layout space so the
          search input does not shift when `isSearching` toggles. */}
      <div
        className="flex items-center text-sm text-gray-500"
        style={{ width: hasScopeFilters ? '24px' : '110px', visibility: isSearching ? 'visible' : 'hidden' }}
        aria-hidden={!isSearching}
        data-testid="orders-search-indicator"
      >
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
        {!hasScopeFilters && 'Searching...'}
      </div>

      {/* ORDER-FILTERS-001: TL-only Client Code / Product dropdowns, inline with Search */}
      {hasScopeFilters && (
        <OrderScopeFilters
          clientCode={scopeFilters.client_code}
          clientProduct={scopeFilters.client_product}
          onChange={handleScopeFilterChange}
          options={scopeFacets}
        />
      )}

      {/* Admin-only Search Latency badge (Feb 28, 2026).
          Surfaces query duration + the execution path the backend
          took so the Ops team can validate the search-index rollout
          in real time. Gated strictly on role === 'admin'. */}
      <SearchLatencyBadge
        user={user}
        meta={searchMeta}
        isSearching={isSearching}
      />
    </div>
  ) : null;

  return (
    <div className="space-y-3" data-testid={isOrderDeskView ? 'order-desk-dashboard' : undefined}>
      {/* DASH-COMPACT-001 (Sep 2026): Team Today strip sits directly under the KPI pill row;
          filters + search + actions are grouped in ONE connected panel below it. */}
      {!isOrderDeskView && nRole(user?.role) !== 'admin' && (
        <OperationalDashboard userId={user?.id} onFilterStatus={(s) => {
          if (validStatuses.includes(s)) setStatusFilter(s);
        }} />
      )}
      <div className={isOrderDeskView ? undefined : 'rounded-2xl border border-blue-100 bg-gradient-to-br from-sky-50/90 via-white to-blue-50/70 shadow-[0_8px_24px_rgba(37,99,235,0.08)] px-4 pt-3.5 pb-3 space-y-2.5'}
        data-testid={isOrderDeskView ? undefined : 'dashboard-control-panel'}>
      {/* Year Filter and Admin Order Type Filter - Using Extracted Component */}
      {/* OD-COMPACT-001: for the Order Desk presentation the Year filter lives inside the toolbar below */}
      {!isOrderDeskView && (
        <OrderFiltersBar
          embedded
          user={user}
          yearFilter={yearFilter}
          setYearFilter={setYearFilter}
          availableYears={availableYears}
          loadingYears={loadingYears}
          orderTypeFilter={orderTypeFilter}
          setOrderTypeFilter={setOrderTypeFilter}
          unassignedOnly={unassignedOnly}
          setUnassignedOnly={setUnassignedOnly}
          mlsView={mlsView}
          onMlsViewSwitch={handleMlsViewSwitch}
          onFilterChange={({ year, orderType, unassignedOnly: unassigned }) => {
            setCurrentPage(1);
            
            // ORDER-FILTERS-001: Order Type changes the product universe — reset scope filters
            if (orderType !== undefined && orderType !== orderTypeFilter) {
              scopeFiltersRef.current = { client_code: '', client_product: '' };
              setScopeFilters({ client_code: '', client_product: '' });
            }

            // TYPING-SCOPE-001: Typing tab is hidden under Tax Only / MLS —
            // clear a stale 'typing' status filter when entering those views.
            // MLS-UNIFIED-STATUS-001: the MLS view has its own unified tab set —
            // entering/leaving it resets the status tab to All.
            const _mlsCtxFlip = orderType !== undefined && (orderType === 'mls') !== (orderTypeFilter === 'mls');
            const clearStaleTyping = (orderType !== undefined && ['tax', 'mls'].includes(orderType) && statusFilter === 'typing')
              || (_mlsCtxFlip && statusFilter !== 'all');
            if (clearStaleTyping) {
              setStatusFilter('all');
            }

            // Update URL params for filter persistence
            const updates = {};
            if (year !== undefined) updates.year = year;
            if (orderType !== undefined) {
              updates.type = orderType;
              if (orderType !== orderTypeFilter) { updates.client_code = ''; updates.product = ''; }
              if (clearStaleTyping) updates.status = 'all';
            }
            if (unassigned !== undefined) updates.unassigned = unassigned ? 'true' : 'false';
            updateURLParams(updates);
            
            fetchOrders(
              1, 
              searchQuery, 
              clearStaleTyping ? 'all' : statusFilter, 
              orderType !== undefined ? orderType : orderTypeFilter, 
              priorityFilter, 
              year !== undefined ? year : yearFilter,
              unassigned !== undefined ? unassigned : unassignedOnly
            );
            // Propagate unassignedOnly change to parent for KPI card sync
            if (unassigned !== undefined && onUnassignedOnlyChange) {
              onUnassignedOnlyChange(unassigned);
            }
            // May 5, 2026 — propagate year to parent so stats cards apply
            // the same year filter and stay in sync with the table.
            if (year !== undefined && onYearChange) {
              onYearChange(year);
            }
          }}
          onOrderTypeFilterChange={onOrderTypeFilterChange}
        />
      )}
      
      {/* Action Buttons - Role-based restrictions.
          OD-COMPACT-001: for the Order Desk presentation this row becomes a compact
          toolbar (Year | New Order | Reports | Bulk Upload) with the search row attached. */}
      <div
        className={isOrderDeskView ? 'rounded-xl border border-slate-200 bg-white shadow-sm' : 'flex flex-col gap-3'}
        data-testid={isOrderDeskView ? 'order-desk-toolbar' : undefined}
      >
        <div className={isOrderDeskView ? 'flex flex-wrap items-center gap-2.5 px-4 py-2.5' : 'flex flex-wrap items-center gap-3'}>
        {/* DASH-STABLE-001: row A = search + scope filters (left) and primary actions (right);
            row B = assignment / priority controls. Fixed rows → selecting orders never reflows. */}
        {hasScopeFilters && searchRow}
        {isOrderDeskView && (
          <>
      <OrderFiltersBar
              compact
              user={user}
              yearFilter={yearFilter}
              setYearFilter={setYearFilter}
              availableYears={availableYears}
              loadingYears={loadingYears}
              orderTypeFilter={orderTypeFilter}
              setOrderTypeFilter={setOrderTypeFilter}
              unassignedOnly={unassignedOnly}
              setUnassignedOnly={setUnassignedOnly}
              mlsView={mlsView}
              onMlsViewSwitch={handleMlsViewSwitch}
              onFilterChange={({ year, orderType, unassignedOnly: unassigned }) => {
                setCurrentPage(1);
                
                // ORDER-FILTERS-001: Order Type changes the product universe — reset scope filters
                if (orderType !== undefined && orderType !== orderTypeFilter) {
                  scopeFiltersRef.current = { client_code: '', client_product: '' };
                  setScopeFilters({ client_code: '', client_product: '' });
                }

                // TYPING-SCOPE-001: Typing tab is hidden under Tax Only / MLS —
                // clear a stale 'typing' status filter when entering those views.
                // MLS-UNIFIED-STATUS-001: the MLS view has its own unified tab set —
                // entering/leaving it resets the status tab to All.
                const _mlsCtxFlip = orderType !== undefined && (orderType === 'mls') !== (orderTypeFilter === 'mls');
                const clearStaleTyping = (orderType !== undefined && ['tax', 'mls'].includes(orderType) && statusFilter === 'typing')
                  || (_mlsCtxFlip && statusFilter !== 'all');
                if (clearStaleTyping) {
                  setStatusFilter('all');
                }

                // Update URL params for filter persistence
                const updates = {};
                if (year !== undefined) updates.year = year;
                if (orderType !== undefined) {
                  updates.type = orderType;
                  if (orderType !== orderTypeFilter) { updates.client_code = ''; updates.product = ''; }
                  if (clearStaleTyping) updates.status = 'all';
                }
                if (unassigned !== undefined) updates.unassigned = unassigned ? 'true' : 'false';
                updateURLParams(updates);
                
                fetchOrders(
                  1, 
                  searchQuery, 
                  clearStaleTyping ? 'all' : statusFilter, 
                  orderType !== undefined ? orderType : orderTypeFilter, 
                  priorityFilter, 
                  year !== undefined ? year : yearFilter,
                  unassigned !== undefined ? unassigned : unassignedOnly
                );
                // Propagate unassignedOnly change to parent for KPI card sync
                if (unassigned !== undefined && onUnassignedOnlyChange) {
                  onUnassignedOnlyChange(unassigned);
                }
                // May 5, 2026 — propagate year to parent so stats cards apply
                // the same year filter and stay in sync with the table.
                if (year !== undefined && onYearChange) {
                  onYearChange(year);
                }
              }}
              onOrderTypeFilterChange={onOrderTypeFilterChange}
            />
            <div className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />
          </>
        )}
        {canCreateOrders && (
          <>
            <Dialog open={showCreateOrder} onOpenChange={handleCreateOrderOpenChange}>
              <DialogTrigger asChild>
                <Button data-testid="new-order-button" size={isOrderDeskView ? 'sm' : 'default'}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Order
                </Button>
              </DialogTrigger>
              <DialogContent
                className="co-dialog max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0 rounded-2xl border-0 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.45)]"
                hideCloseButton
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => {
                  if (createOrderDirtyRef.current) {
                    e.preventDefault();
                    setShowDiscardConfirm(true);
                  }
                }}
              >
                {/* TITLEFLOW-CREATE-ORDER-UI-002 — premium gradient header (fixed; only the form body scrolls) */}
                <DialogHeader className="relative flex-shrink-0 overflow-hidden bg-gradient-to-r from-[#2563eb] via-[#4f46e5] to-[#7c3aed] px-5 py-4 text-left" data-testid="create-order-header">
                  <div aria-hidden className="pointer-events-none absolute inset-y-0 right-14 hidden w-60 sm:block">
                    <div className="absolute right-24 -top-8 h-32 w-32 rounded-full bg-violet-300/30 blur-2xl" />
                    <div className="absolute right-20 top-2 h-20 w-16 rotate-[-10deg] rounded-md bg-white/15 ring-1 ring-white/30">
                      <div className="mt-3 ml-2.5 space-y-1.5"><div className="h-1 w-9 rounded bg-white/50" /><div className="h-1 w-7 rounded bg-white/40" /><div className="h-1 w-8 rounded bg-white/30" /></div>
                    </div>
                    <div className="absolute right-6 top-4 h-20 w-16 rotate-[12deg] rounded-md bg-white/25 ring-1 ring-white/40 backdrop-blur-sm">
                      <div className="mt-3 ml-2.5 space-y-1.5"><div className="h-1 w-9 rounded bg-white/60" /><div className="h-1 w-6 rounded bg-white/50" /><div className="h-1 w-8 rounded bg-white/40" /></div>
                      <span className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-violet-500 text-white ring-2 ring-white/70"><Plus className="h-3 w-3" strokeWidth={3} /></span>
                    </div>
                  </div>
                  <div className="relative flex items-center gap-3.5 pr-10">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white shadow-[inset_0_1px_0_rgba(255,255,255,.35)] ring-1 ring-white/30 backdrop-blur-sm">
                      <FilePlus2 className="h-6 w-6" />
                    </span>
                    <div className="min-w-0">
                      <DialogTitle className="text-xl font-bold leading-tight tracking-tight text-white">Create New Order</DialogTitle>
                      <DialogDescription className="mt-0.5 text-sm text-blue-100/90">Enter order details and select the required product.</DialogDescription>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCreateOrderOpenChange(false)}
                    aria-label="Close"
                    className="co-close absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-white ring-1 ring-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                    data-testid="create-order-close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto bg-slate-50/70 px-5 pt-3" style={{ '--co-gutter': '20px' }} data-testid="create-order-body">
                  <CreateOrderForm 
                    onSubmit={handleCreateOrder}
                    onDirtyChange={(dirty) => { createOrderDirtyRef.current = dirty; }}
                    onCancel={() => handleCreateOrderOpenChange(false)}
                  />
                </div>
              </DialogContent>
            </Dialog>

            {/* Unsaved changes confirmation modal */}
            <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
              <AlertDialogContent data-testid="discard-confirm-modal">
                <AlertDialogHeader>
                  <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    You have unsaved changes. If you close now, your changes will be lost.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="discard-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="discard-confirm-btn"
                    onClick={handleDiscardConfirm}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Discard &amp; Close
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}

            {/* Reports Button - role OR matrix reports:view (OD-PARITY-001) */}
            {canViewReports && (
              <Button onClick={() => setActiveView('reports')} data-testid="reports-button" size={isOrderDeskView ? 'sm' : 'default'} variant={isOrderDeskView ? 'outline' : 'default'}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Reports
              </Button>
            )}

            {/* Bulk Upload Button - role OR matrix orders:bulk_upload (OD-PARITY-001) */}
            {canBulkUpload && (
              <Button 
                onClick={() => setShowBulkUploadModal(true)}
                data-testid="bulk-upload-button"
                size={isOrderDeskView ? 'sm' : 'default'}
                variant={isOrderDeskView ? 'outline' : 'default'}
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Upload
              </Button>
            )}

            {/* DASH-STABLE-001: forced line break → assignment controls always on their own row */}
            {!isOrderDeskView && <div className="basis-full h-0 -my-1.5" aria-hidden="true" />}

            {/* Bulk Assignment UI - Using Extracted Component */}
            <BulkAssignmentControls
              key={isTaxTL ? `tl-${mlsView}` : 'std'}
              user={user}
              selectedOrders={selectedOrders}
              setSelectedOrders={setSelectedOrders}
              selectedOrderIds={selectedOrderIds}
              setSelectedOrderIds={setSelectedOrderIds}
              processors={processors}
              availableProcessors={availableProcessors}
              bulkAssignee={bulkAssignee}
              setBulkAssignee={setBulkAssignee}
              onAssign={typingAssignMode ? handleTypingBulkAssign : handleAdminBulkAssign}
              onAssignToMe={handleAssignToMe}
              typingMode={typingAssignMode}
              typingProcessors={typingProcessors}
              typingListState={typingListState}
              processorListState={processorListState}
              isBulkAssigning={isBulkAssigning}
              normalizeProcessorFn={normalizeProcessor}
              orderTypeFilter={orderTypeFilter}
              orders={orders}
              onApplyMls={handleApplyMlsComponents}
            />

            {/* Bulk Priority UI (Jun 2026) — sits next to Assign control so
                TLs can flip Rush/Urgent on multiple orders in one click. */}
            <BulkPriorityControls
              user={user}
              selectedOrderIds={selectedOrderIds}
              selectedOrders={selectedOrders}
              onCompleted={() => {
                // Refresh dashboard so new priority chips render immediately
                fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly);
                // Clear selections (mirrors bulk-assign post-action behaviour)
                if (setSelectedOrderIds) setSelectedOrderIds(new Set());
                if (setSelectedOrders) setSelectedOrders(new Set());
              }}
            />

            {/* Clarification Sent — bulk action, only in Clarification tab */}
            {statusFilter === 'clarification' && (selectedOrders.size > 0 || selectedOrderIds.size > 0) && (() => {
              const selIds = selectedOrderIds.size > 0 ? [...selectedOrderIds] : [...selectedOrders];
              const anyAlreadyMarked = orders.some(o => selIds.includes(o.id) && o.clarification_sent);
              return (
                <div className="flex items-center gap-2 ml-2">
                  <Button
                    data-testid="bulk-mark-clarification-sent"
                    size="sm"
                    variant="destructive"
                    className="h-8 gap-1.5 text-xs"
                    disabled={isBulkClarificationSending}
                    onClick={() => handleBulkClarificationSent(true)}
                  >
                    <Flag size={13} />
                    {isBulkClarificationSending ? 'Marking...' : `Mark Clarification Sent (${selectedOrders.size || selectedOrderIds.size})`}
                  </Button>
                  {anyAlreadyMarked && (
                    <Button
                      data-testid="bulk-unmark-clarification-sent"
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs"
                      disabled={isBulkClarificationSending}
                      onClick={() => handleBulkClarificationSent(false)}
                    >
                      Unmark Sent
                    </Button>
                  )}
                </div>
              );
            })()}

        {/* New Customer - Only available to Vendor Manager role (not Admin or Order Desk) */}
        {nRole(user?.role) === 'vendor_manager' && (
          <Dialog open={showCreateCustomer} onOpenChange={setShowCreateCustomer}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <User className="h-4 w-4 mr-2" />
                New Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Create New Customer</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-2">
                <CreateCustomerForm onSubmit={handleCreateCustomer} />
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>
        {/* OD-COMPACT-001: search row attached to the toolbar */}
        {isOrderDeskView && (
          <div className="border-t border-slate-100 px-4 py-2.5" data-testid="order-desk-search-row">
            <div className="flex gap-3 items-center w-full flex-nowrap">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="order-search"
              type="text"
              placeholder="Enter Order #, Product ID, Borrower Name, Property Address, or Client Code..."
              value={searchInputDisplay}
              onChange={(e) => setSearchInputDisplay(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="pl-10 pr-10 w-full h-9 text-sm bg-slate-50/60 focus:bg-white"
              disabled={isSearching}
            />
            {/* Clear button inside input.
                Feb 19, 2026 — `onMouseDown` (not `onClick`) so the
                click fires BEFORE the input's blur event,
                preventing the input from briefly losing focus
                and re-painting. */}
            {searchInputDisplay && (
              <button
                onMouseDown={(e) => {
                  if (isSearching) return;
                  e.preventDefault();
                  handleClearSearch();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
                disabled={isSearching}
                aria-label="Clear search"
                data-testid="order-desk-search-clear-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Fixed-width spinner slot — reserves layout space so
              the search input does not shift when `isSearching`
              toggles between renders. */}
          <div
            className="flex items-center text-sm text-gray-500 flex-shrink-0"
            style={{ width: '110px', visibility: isSearching ? 'visible' : 'hidden' }}
            aria-hidden={!isSearching}
            data-testid="order-desk-search-indicator"
          >
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Searching...
          </div>
            </div>
          </div>
        )}
      </div>

      {/* Search Bar - Admin + generic Team Leader (TL dashboards render it inside the toolbar row) */}
      {!hasScopeFilters && searchRow}

      {/* ORDER-FILTERS-001: active scope chips + Clear All (renders only when a filter is set) */}
      {hasScopeFilters && (
        <OrderScopeChips
          clientCode={scopeFilters.client_code}
          clientProduct={scopeFilters.client_product}
          onChange={handleScopeFilterChange}
        />
      )}
      </div>

      {/* Orders Table */}
      <Card className={isOrderDeskView ? 'rounded-xl border-slate-200 shadow-sm overflow-hidden' : 'ops-orders-card rounded-xl border-slate-200 shadow-sm'}>
        {/* MLS-LAYOUT-001 (Aug 2026): two-row filter hierarchy —
            Row 1: workflow/status tabs on one clean line;
            Row 2 (right-aligned): MLS state (when applicable) + Priority. */}
        <CardHeader className={isOrderDeskView ? 'flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 px-4 py-2.5 border-b border-slate-100 bg-slate-50/40' : 'flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 px-4 py-2.5'}>
          {/* MLS-DASHBOARD-TAB-UI-002: tabs take the remaining width and wrap INSIDE their own box; Priority is pinned
              far-right on the same row (shrink-0 + ml-auto) in both TAX and MLS views, so switching views no longer
              pushes the grid down by a wrapped filter row. */}
          <CardTitle className="flex items-center gap-2 flex-1 min-w-0">
            {/* Status Filter Tabs */}
            <div className="flex gap-2 flex-wrap mt-0">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.value}
                  data-testid={`orders-status-tab-${tab.value}`}
                  onClick={() => {
                    setStatusFilter(tab.value);
                    setCurrentPage(1);
                    // Update URL with status parameter for persistence across refresh
                    updateURLParams({ status: tab.value });
                    fetchOrders(1, searchQuery, tab.value, orderTypeFilter, priorityFilter, yearFilter);
                  }}
                  className={`${isOrderDeskView ? 'px-3 py-1.5 text-xs' : 'px-3 py-2 text-sm'} rounded-lg font-medium transition-all ${
                    statusFilter === tab.value 
                      ? `${tab.color} ring-2 ring-offset-1 ring-blue-500` 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardTitle>
          <div className="flex items-center justify-end gap-2 ml-auto shrink-0" data-testid="orders-priority-filter">
            {/* Priority Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Priority:</span>
              <Select value={priorityFilter} onValueChange={(value) => {
                setPriorityFilter(value);
                setCurrentPage(1);
                updateURLParams({ priority: value });
                fetchOrders(1, searchQuery, statusFilter, orderTypeFilter, value, yearFilter);
              }}>
                <SelectTrigger className="w-[130px] h-9 text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="urgent">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Urgent
                    </span>
                  </SelectItem>
                  <SelectItem value="rush">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Rush
                    </span>
                  </SelectItem>
                  <SelectItem value="normal">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                      Normal
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 orders-table-wrap" style={{ width: '100%', maxWidth: '100%' }}>
          <div className={`table-container ${nRole(user?.role) === 'admin' ? 'orders-scroll' : ''}`}>
            <OrdersTable
              orders={orders}
              loading={loading}
              fetchError={fetchError}
              onRetry={() => fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter, unassignedOnly)}
              searchTerm={(searchQuery || '').trim()}
              visibleColumns={visibleOrderColumns}
              user={user}
              selectedOrders={selectedOrders}
              selectedOrderIds={selectedOrderIds}
              availableProcessors={availableProcessors}
              tableDensity={tableDensity}
              tableRef={orderTableRef}
              isResizing={isOrderTableResizing}
              resizingColumn={resizingOrderColumn}
              onResizeStart={handleOrderResizeStart}
              onAutoFit={handleOrderAutoFit}
              onKeyboardResize={handleOrderKeyboardResize}
              onAdminSelectAll={handleAdminSelectAll}
              onAdminSelectOrder={handleAdminSelectOrder}
              onSelectAll={handleSelectAll}
              onSelectOrder={handleSelectOrder}
              onOrderNumberClick={handleOrderNumberClick}
              canOpenOrderViaMatrix={canOpenOrderViaMatrix}
              isOrderDeskView={isOrderDeskView}
              inMlsContext={inMlsContext}
              sortConfig={sortConfig}
              onSort={handleSort}
              formatPropertyAddress={formatPropertyAddress}
              getBorrowerName={getBorrowerName}
              getPropertyCounty={getPropertyCounty}
              getPropertyState={getPropertyState}
            />
          </div>
        </CardContent>
        
        {/* Pagination Controls */}
        <PaginationControls
          currentPage={currentPage}
          totalOrders={totalOrders}
          ordersPerPage={ORDERS_PER_PAGE}
          ordersCount={orders.length}
          loading={loading}
          onPageChange={(page) => fetchOrders(page, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter)}
        />
      </Card>

      {/* Modern Order Edit Modal — Order Desk, Admin, and Team Leaders */}
      {(['order_desk', 'admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(nRole(user?.role)) || canEditOrdersViaMatrix) && selectedOrder && (
        <OrderDeskEditModal
          isOpen={showOrderDetails}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onUpdate={handleUpdateOrder}
        />
      )}

      {/* Bulk Assignment Modal for Team Leaders and Admin */}
      {(['title_search_team_leader', 'tax_search_team_leader', 'admin'].includes(nRole(user?.role))) && (
        <Dialog open={showBulkAssignModal} onOpenChange={setShowBulkAssignModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Orders</DialogTitle>
              <DialogDescription>
                Assign {selectedOrderIds.size} selected order{selectedOrderIds.size !== 1 ? 's' : ''} to a processor.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Processor</label>
                <Select
                  value={bulkAssignProcessor}
                  onValueChange={setBulkAssignProcessor}
                  disabled={isBulkAssigning}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a processor or unassign..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-600">None</span>
                        <span className="text-xs text-muted-foreground">(Unassign orders)</span>
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                    {availableProcessors.map((processor) => (
                      <SelectItem key={processor.id} value={processor.id}>
                        {processor.full_name || processor.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedOrderIds.size > 0 && (
                <div className="text-sm text-muted-foreground">
                  <strong>Selected Orders:</strong>
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    {Array.from(selectedOrderIds).map((orderId, index) => {
                      const order = orders.find(o => o.id === orderId);
                      return (
                        <div key={orderId} className="text-xs py-1">
                          {order?.order_number || orderId} - {order?.client_product}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkAssignModal(false);
                  setBulkAssignProcessor('');
                }}
                disabled={isBulkAssigning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAssign}
                disabled={!bulkAssignProcessor || selectedOrderIds.size === 0 || isBulkAssigning}
              >
                {isBulkAssigning ? 'Processing...' : 
                 bulkAssignProcessor === 'none' 
                   ? `Unassign ${selectedOrderIds.size} Order${selectedOrderIds.size !== 1 ? 's' : ''}` 
                   : `Assign ${selectedOrderIds.size} Order${selectedOrderIds.size !== 1 ? 's' : ''}`
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Custom Assign Orders Modal - with confirm/progress/success states */}
      <AssignOrdersModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignModalState('confirm');
        }}
        onConfirm={executeAdminBulkAssign}
        orderCount={assignModalData.orderCount}
        assigneeName={assignModalData.assigneeName}
        state={assignModalState}
      />
      
      {/* Order Summary Popup - for Tax Search / Update Tax Search / Search Products */}
      <OrderSummaryPopup
        isOpen={orderSummaryPopup.isOpen}
        onClose={() => setOrderSummaryPopup({ isOpen: false, orderData: null })}
        orderData={orderSummaryPopup.orderData}
        currentUser={user}
        onUpdate={() => {
          // Refresh the order data after updates - pass current page and filters
          fetchOrders(currentPage, searchQuery, statusFilter, orderTypeFilter, priorityFilter, yearFilter);
          if (refreshStats) {
            refreshStats();
          }
        }}
        showRequestEditButton={orderSummaryPopup.showRequestEditButton}
        onRequestEdit={orderSummaryPopup.onRequestEdit}
        showEditOrderButton={orderSummaryPopup.showEditOrderButton}
        onEditOrder={orderSummaryPopup.onEditOrder}
      />
      
      {/* Edit Request Modal - for Order Desk users to request changes on assigned orders */}
      <EditRequestModal
        isOpen={editRequestModal.isOpen}
        onClose={() => setEditRequestModal({ isOpen: false, orderData: null })}
        orderData={editRequestModal.orderData}
        onSuccess={() => {
          fetchOrders();
          if (refreshStats) {
            refreshStats();
          }
        }}
      />

      {/* Bulk Upload Modal - CSV import for Admin and Order Desk */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={(result) => {
          toast.success(`Successfully created ${result.created_count} orders`);
          fetchOrders(); // Refresh order list
          if (refreshStats) {
            refreshStats();
          }
        }}
        token={localStorage.getItem('token')}
      />
      
      {/* Duplicate Order Warning Modal */}
      <DuplicateOrderModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setPendingOrderData(null);
          setPendingEditData(null);
          setDuplicateOrder(null);
          setDuplicateIsTerminal(false);
        }}
        existingOrder={duplicateOrder}
        onViewExisting={handleViewExistingOrder}
        onOverride={handleOverrideDuplicate}
        terminal={duplicateIsTerminal}
        // DUP-OVERRIDE-DUAL-ROLE-001: same additive rule as order creation
        // (Admin / Order Desk role OR matrix orders:create) — mirrors the backend
        // `rbac.orders_pilot.can_override_duplicates` gate.
        canOverride={canCreateOrders}
      />
      
      {/* Scroll to Top Button - appears on long pages */}
      <ScrollToTopButton />
    </div>
  );
};

export default OrdersManagement;
